/**
 * Neo Collection Grid - GraphQL-powered collection page
 * Handles: Product fetching, filtering, sorting, and rendering
*/

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Configuration and Initial State                                         │
  └─────────────────────────────────────────────────────────────────────────┘
*/
const maxVisibileSizeFilters = 12;

const config = globalThis.neoCollectionConfig;

const state = {
  firstLoad: true,
  allProducts: [],
  filteredProducts: [],
  filters: [],
  /** Snapshot of activeFilters + price when state.filters was last set from API; used to avoid showing stale counts */
  filtersContext: null,
  activeFilters: {},
  priceRange: { min: null, max: null, currentMin: 0, currentMax: 0 },
  pagination: {
    cursor: null,
    hasNextPage: false,
    isLoading: false,
    totalLoaded: 0
  },
  sortOrder: neoCollectionConfig.sortOrder || 'manual',
  currentSort: { sortValue: null, sortKey: null, reverse: false }, // Store current sort state
  queryParams: {}, // Store all query parameters to maintain them
  cache: {}, // Cache for filtered product results
  bestsellers: [], // Store bestseller products
  custom7thAnd10thProducts: [], // Store custom 7th and 10th products
  totalRenderedProducts: config.productsRenderedCount || 0, // Track total rendered products (starts with liquid-rendered count)
  inGridSizeFilterInserted: false, // Track if in-grid size filter is inserted
  buzzInTownSectionInserted: false // Track if buzz in town section is inserted
};

const isMobile = globalThis.innerWidth < 768;
const sortDropdownContainer = document.querySelector('[data-id="desktop-sort-container"]');
const mobileSortDropdownContainer = document.querySelector('[data-id="mobile-sort-dropdown-container"]');

const buzzInTownSwiperOptionsDesktop = {
  slidesPerView: 3,
  spaceBetween: 0,
  centeredSlides: true,
  initialSlide: 1,
  navigation: {
    nextEl: '.buzz-in-town-section-swiper-container .swiper-button-next',
    prevEl: '.buzz-in-town-section-swiper-container .swiper-button-prev',
  }
}

const buzzInTownSwiperOptionsMobile = {
  effect: 'cards',
  grabCursor: true,
  centeredSlides: true,
  initialSlide: 2
}

if (!isMobile) {
  const filterDrawerApplyButton = document.querySelector('[data-id="filter-drawer-apply-button"]');
  filterDrawerApplyButton.innerHTML = 'Apply Changes';
}

/**
 * Generate HTML for buzz in town section
 * @returns {string} HTML string for the buzz in town section
 */
function generateBuzzInTownSectionHTML() {
  if (!config.buzzInTownSection?.html) {
    return '';
  }

  return config.buzzInTownSection.html;
}

/**
 * Initialize buzz in town swiper after section is inserted
*/
function initializeBuzzInTownSwiper() {
  const swiperContainer = document.querySelector('#product-grid .buzz-in-town-section-swiper-container .swiper');
  if (typeof Swiper === 'undefined') {
    return;
  }
  if (!swiperContainer) {
    return;
  }

  // Determine which swiper options to use based on device size
  const swiperOptions = isMobile ? buzzInTownSwiperOptionsMobile : buzzInTownSwiperOptionsDesktop;
  globalThis.buzzInTownSwiper = new Swiper(swiperContainer, swiperOptions);
  swiperContainer.dataset.swiperInitialized = 'true';
  buzzInTownSwiper.on('slideChange', () => {
    let mobileThumbIcon = document.querySelector('.buzz-in-town-section .parent-div-buzz-in-town-mobile-thumb-icon');
    if (mobileThumbIcon) {
      mobileThumbIcon.remove();
    }
  });
  // Mark swiper as initialized
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ URL Parameters Parsing                                                  │
  └─────────────────────────────────────────────────────────────────────────┘
*/
function parseURLFilters() {
  const urlParams = new URLSearchParams(globalThis.location.search);
  const parsedFilters = {};
  let hasPriceFilter = false;
  let priceMin = null;
  let priceMax = null;

  // Store all query parameters in state.queryParams
  state.queryParams = {};
  urlParams.forEach((value, key) => {
    state.queryParams[key] = value;
  });

  urlParams.forEach((value, key) => {
    // Skip empty values (but keep sort_by and other non-filter params)
    if (!value && key !== 'sort_by') return;

    // Handle sort_by parameter
    if (key === 'sort_by' && value) {
      state.currentSort.sortValue = value;
      return;
    }

    // Handle price filters
    if (key === 'filter.v.price.gte') {
      priceMin = Number.parseFloat(value);
      hasPriceFilter = true;
      return;
    }
    if (key === 'filter.v.price.lte') {
      priceMax = Number.parseFloat(value);
      hasPriceFilter = true;
      return;
    }

    // Handle regular filters (filter.p.m.custom.*, filter.v.option.*, etc.)
    if (key.startsWith('filter.')) {
      // Extract filter ID from key (e.g., "filter.p.m.custom.product_type" -> "filter.p.m.custom.product_type")
      const filterId = key;

      // Decode URL-encoded values (e.g., "Active+Bras" -> "Active Bras")
      const decodedValue = decodeURIComponent(value.replaceAll('+', ' '));

      // Initialize array if filter doesn't exist
      if (!parsedFilters[filterId]) {
        parsedFilters[filterId] = [];
      }

      // Add value to filter array (handle comma-separated values)
      const values = decodedValue.split(',').map(v => v.trim()).filter(Boolean);
      parsedFilters[filterId].push(...values);
    }
  });

  // Set price range if filters are present
  if (hasPriceFilter) {
    state.priceRange.currentMin = priceMin ?? state.priceRange.min;
    state.priceRange.currentMax = priceMax ?? state.priceRange.max;
  }

  // Set active filters
  state.activeFilters = parsedFilters;

  return Object.keys(parsedFilters).length > 0 || hasPriceFilter;
}

function buildGraphQLFiltersFromActiveFilters() {
  const graphqlFilters = [];

  Object.entries(state.activeFilters).forEach(([filterId, values]) => {
    values.forEach(value => {
      // Find the original filter to get the input format
      const filter = state.filters.find(f => f.id === filterId);
      if (filter) {
        const filterValue = filter.values.find(v => v.label === value);
        if (filterValue?.input) {
          try {
            graphqlFilters.push(JSON.parse(filterValue.input));
          } catch (e) {
            console.error('Error parsing filter input:', e);
          }
        } else if (filterId.startsWith('filter.p.m.custom.')) {
          // If filter value not found in filters (happens before filters are loaded),
          // construct the filter input manually based on filter ID pattern
          const metafieldKey = filterId.replace('filter.p.m.custom.', '');
          graphqlFilters.push({
            productMetafield: {
              namespace: 'custom',
              key: metafieldKey,
              value: value
            }
          });
        } else if (filterId.startsWith('filter.v.option.')) {
          const optionName = filterId.replace('filter.v.option.', '');
          graphqlFilters.push({
            variantOption: {
              name: optionName,
              value: value
            }
          });
        }
      } else if (filterId.startsWith('filter.p.m.custom.')) {
        // Filter not found in state.filters yet (before first fetch), construct manually
        const metafieldKey = filterId.replace('filter.p.m.custom.', '');
        graphqlFilters.push({
          productMetafield: {
            namespace: 'custom',
            key: metafieldKey,
            value: value
          }
        });
      } else if (filterId.startsWith('filter.v.option.')) {
        const optionName = filterId.replace('filter.v.option.', '');
        graphqlFilters.push({
          variantOption: {
            name: optionName,
            value: value
          }
        });
      }
    });
  });

  // Add price filter if modified
  if (state.priceRange.currentMin > state.priceRange.min ||
      state.priceRange.currentMax < state.priceRange.max) {
    graphqlFilters.push({
      price: {
        min: state.priceRange.currentMin,
        max: state.priceRange.currentMax
      }
    });
  }

  return graphqlFilters;
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Cache Management                                                        │
  └─────────────────────────────────────────────────────────────────────────┘
*/

/**
 * Generate a cache key from the current filter state
 * @returns {string} Cache key representing the filter combination
 */
function generateCacheKey() {
  // Sort filter entries for consistent cache keys
  const sortedFilters = Object.entries(state.activeFilters)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([filterId, values]) => {
      // Sort values for consistency
      const sortedValues = [...values].sort((a, b) => a.localeCompare(b)).join(',');
      return `${filterId}:${sortedValues}`;
    })
    .join('|');

  // Include price range if modified
  const priceKey = (state.priceRange.currentMin > state.priceRange.min || state.priceRange.currentMax < state.priceRange.max) ? `price:${state.priceRange.currentMin}-${state.priceRange.currentMax}` : '';

  // Include sort order if it affects results
  const sortKey = state.sortOrder ? `sort:${state.sortOrder}` : '';

  // Combine all parts
  const keyParts = [sortedFilters, priceKey, sortKey].filter(Boolean);
  return keyParts.length > 0 ? keyParts.join('|') : 'no-filters';
}

/**
 * Get cached products for the current filter combination
 * @returns {Object|null} Cached result or null if not found
 */
function getCachedProducts() {
  const cacheKey = generateCacheKey();
  const cachedResult = state.cache[cacheKey] || null;
  return cachedResult;
}

/**
 * Store products in cache for the current filter combination
 * @param {Object} result - The result object containing products, filters, and pageInfo
 */
function setCachedProducts(result) {
  const cacheKey = generateCacheKey();
  state.cache[cacheKey] = {
    products: result.products,
    filters: result.filters,
    pageInfo: result.pageInfo,
    timestamp: Date.now()
  };
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ URL Query Parameters Update                                             │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function buildQueryString() {
  const queryParts = [];
  const nonFilterParams = [];

  // Helper function to encode value (replace spaces with +)
  const encodeValue = (value) => {
    return String(value).replaceAll(' ', '+');
  };

  // Helper function to add query parameter
  const addParam = (key, value) => {
    if (value !== null && value !== undefined) {
      queryParts.push(`${key}=${encodeValue(value)}`);
    }
  };

  // Separate filter params from non-filter params
  Object.entries(state.queryParams).forEach(([key, value]) => {
    // Only preserve non-filter params (excluding sort_by which we handle separately)
    if (!key.startsWith('filter.') && key !== 'filter.v.price.gte' && key !== 'filter.v.price.lte' && key !== 'sort_by') {
      nonFilterParams.push({ key, value });
    }
  });

  // Add active filters - each value as a separate parameter (filters first)
  Object.entries(state.activeFilters).forEach(([filterId, values]) => {
    if (values.length > 0) {
      // Add each value as a separate parameter with the same key
      values.forEach(value => {
        addParam(filterId, value);
      });
    }
  });

  // Add price filters
  // Always include price filters if they were in the original query or if they're modified
  const hasPriceFilterInQuery = 'filter.v.price.gte' in state.queryParams || 'filter.v.price.lte' in state.queryParams;
  const isPriceModified = state.priceRange.currentMin > state.priceRange.min || state.priceRange.currentMax < state.priceRange.max;

  if (hasPriceFilterInQuery || isPriceModified) {
    if (state.priceRange.currentMin > state.priceRange.min) {
      addParam('filter.v.price.gte', state.priceRange.currentMin.toString());
    } else {
      addParam('filter.v.price.gte', '');
    }
    if (state.priceRange.currentMax < state.priceRange.max) {
      addParam('filter.v.price.lte', state.priceRange.currentMax.toString());
    } else {
      addParam('filter.v.price.lte', '');
    }
  }

  // Add sort_by parameter if sort is active
  if (state.currentSort.sortValue) {
    addParam('sort_by', state.currentSort.sortValue);
  }

  // Add non-filter params last
  nonFilterParams.forEach(({ key, value }) => {
    addParam(key, value);
  });

  return queryParts.join('&');
}

function updateURLQueryParams() {
  const queryString = buildQueryString();
  const newURL = queryString ? `${globalThis.location.pathname}?${queryString}` : globalThis.location.pathname;

  // Update URL without page reload using history API
  globalThis.history.replaceState({}, '', newURL);

  // Update state.queryParams to reflect the new URL
  const urlParams = new URLSearchParams(queryString);
  state.queryParams = {};
  urlParams.forEach((value, key) => {
    state.queryParams[key] = value;
  });
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Utility Functions                                                       │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function hasActiveFilters() {
  return Object.keys(state.activeFilters).length > 0 || state.priceRange.currentMin > state.priceRange.min || state.priceRange.currentMax < state.priceRange.max;
}

function hasColourFilterApplied() {
  const colorFilterId = 'filter.p.m.custom.filter_colour';

  const activeValues = state?.activeFilters?.[colorFilterId];
  if (Array.isArray(activeValues) && activeValues.some(v => String(v ?? '').trim() !== '')) {
    return true;
  }

  const queryValue = state?.queryParams?.[colorFilterId];
  if (String(queryValue ?? '').trim() !== '') {
    return true;
  }

  // Fallback: state parsing may not have run yet (e.g. invoked during initial script eval).
  // Read directly from the URL query string.
  try {
    const search = globalThis?.location?.search || '';
    if (!search) return false;
    const urlParams = new URLSearchParams(search);
    const raw = urlParams.get(colorFilterId);
    const decoded = decodeURIComponent(String(raw ?? '').replaceAll('+', ' ')).trim();
    return decoded !== '';
  } catch (e) {
    return false;
  }
}

function hasFiltersBeenApplied() {
  const hasCache = !!state.cache && Object.keys(state.cache).length > 0;

  const hasActiveFilterValues = !!state.activeFilters && Object.values(state.activeFilters).some((values) => {
    if (Array.isArray(values)) return values.length > 0;
    return Boolean(values);
  });

  const hasPriceFilter = !!state.priceRange &&
    (state.priceRange.currentMin > state.priceRange.min || state.priceRange.currentMax < state.priceRange.max);

  // `state.queryParams` holds the URL query params. Treat only real filter params as “filters applied”.
  // Ignore non-filter params like `sort_by`, `pb`, `view`, etc.
  const hasQueryParamFilters = !!state.queryParams && Object.entries(state.queryParams).some(([key, value]) => {
    if (!key.startsWith('filter.')) return false;
    const str = String(value ?? '').trim();
    return str !== '';
  });

  return hasActiveFilterValues || hasPriceFilter || hasQueryParamFilters || hasCache;
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ GraphQL Queries                                                         │
  └─────────────────────────────────────────────────────────────────────────┘
*/
function getUpdatedproductQueryFields() {
  const productQueryFields = `{
    title
    handle
    id
    tags
    availableForSale
    vendor
    productType
    priceRange {
      maxVariantPrice { amount }
      minVariantPrice { amount }
    }
    variants(first: 1) {
      edges {
        node {
          id
          title
          sku
          barcode
          price { amount }
          compareAtPrice { amount }
        }
      }
    }
    images(first: 5) {
      nodes {
        src
        altText
        height
        width
      }
    }
    options(first: 1) {
      name
      values
    }
    color_variant: metafield(namespace: "custom", key: "color_variant") {
      value
      ${hasColourFilterApplied() ? `
      references(first: 30) {
        nodes {
          ... on Product {
            id
            handle
            title
            tags
            availableForSale
            vendor
            productType
            priceRange {
              maxVariantPrice { amount }
              minVariantPrice { amount }
            }
            variants(first: 1) {
              edges {
                node {
                  id
                  title
                  sku
                  barcode
                  price { amount }
                  compareAtPrice { amount }
                }
              }
            }
            images(first: 5) {
              nodes {
                src
                altText
                height
                width
              }
            }
            options(first: 1) {
              name
              values
            }
            filter_colour: metafield(namespace: "custom", key: "filter_colour") { value }
            colour_family: metafield(namespace: "custom", key: "colour_family") { value }
            style_number: metafield(namespace: "custom", key: "style_number") { value }
            uom_quantity: metafield(namespace: "custom", key: "uom_quantity") { value }
            select_size_guide: metafield(namespace: "custom", key: "select_size_guide") { value }
          }
        }
      }` : ''}
    }
    select_size_guide: metafield(namespace: "custom", key: "select_size_guide") { value }
    style_number: metafield(namespace: "custom", key: "style_number") { value }
    uom_quantity: metafield(namespace: "custom", key: "uom_quantity") { value }
    parent_category: metafield(namespace: "custom", key: "parent_category") { value }
    second_subcategory: metafield(namespace: "custom", key: "second_subcategory") { value }
    child_category: metafield(namespace: "custom", key: "child_category") { value }
    bulk_box_material_code: metafield(namespace: "custom", key: "bulk_box_material_code") { value }
    sap_material_code: metafield(namespace: "custom", key: "sap_material_code") { value }
    colour_family: metafield(namespace: "custom", key: "colour_family") { value }
    jockey_collection: metafield(namespace: "custom", key: "jockey_collection") { value }
    product_color_name: metafield(namespace: "custom", key: "product_color_name") { value }
    fabric: metafield(namespace: "custom", key: "fabric") { value }
    bra_padding_filter: metafield(namespace: "custom", key: "bra_padding_filter") { value }
    pack_multipacks_Filter: metafield(namespace: "custom", key: "pack_multipacks_Filter") { value }
    closure_filter: metafield(namespace: "custom", key: "closure_filter") { value }
    waist_rise: metafield(namespace: "custom", key: "waist_rise") { value }
    bra_wiring_filter: metafield(namespace: "custom", key: "bra_wiring_filter") { value }
    back_style_filter: metafield(namespace: "custom", key: "back_style_filter") { value }
    straps_filter: metafield(namespace: "custom", key: "straps_filter") { value }
    bra_front_coverage_filter: metafield(namespace: "custom", key: "_bra_front_coverage_filter") { value }
    top_rear_coverage_filter: metafield(namespace: "custom", key: "top_rear_coverage_filter") { value }
    rear_coverage_Filter: metafield(namespace: "custom", key: "rear_coverage_Filter") { value }
    filter_colour: metafield(namespace: "custom", key: "filter_colour") { value }
    bestseller_all_applicable_filter_values: metafield(namespace: "custom", key: "bestseller_all_applicable_filter_values") { value }
  }`;

  return productQueryFields;
}

function getUpdatedCustomProductQueryFields() {
  const customProductQueryField = `{
    title
    handle
    id
    tags
    availableForSale
    vendor
    productType
    priceRange {
      maxVariantPrice { amount }
      minVariantPrice { amount }
    }
    variants(first: 1) {
      edges {
        node {
          id
          title
          sku
          barcode
          price { amount }
          compareAtPrice { amount }
        }
      }
    }
    images(first: 5) {
      nodes {
        src
        altText
        height
        width
      }
    }
    options(first: 1) {
      name
      values
    }
    color_variant: metafield(namespace: "custom", key: "color_variant") {
      value
      references(first: 30) {
        nodes {
          ... on Product ${productQueryFields}
        }
      }
    }
    select_size_guide: metafield(namespace: "custom", key: "select_size_guide") { value }
    style_number: metafield(namespace: "custom", key: "style_number") { value }
    uom_quantity: metafield(namespace: "custom", key: "uom_quantity") { value }
    parent_category: metafield(namespace: "custom", key: "parent_category") { value }
    second_subcategory: metafield(namespace: "custom", key: "second_subcategory") { value }
    child_category: metafield(namespace: "custom", key: "child_category") { value }
    bulk_box_material_code: metafield(namespace: "custom", key: "bulk_box_material_code") { value }
    sap_material_code: metafield(namespace: "custom", key: "sap_material_code") { value }
    colour_family: metafield(namespace: "custom", key: "colour_family") { value }
    jockey_collection: metafield(namespace: "custom", key: "jockey_collection") { value }
    product_color_name: metafield(namespace: "custom", key: "product_color_name") { value }
    fabric: metafield(namespace: "custom", key: "fabric") { value }
    bra_padding_filter: metafield(namespace: "custom", key: "bra_padding_filter") { value }
    pack_multipacks_Filter: metafield(namespace: "custom", key: "pack_multipacks_Filter") { value }
    closure_filter: metafield(namespace: "custom", key: "closure_filter") { value }
    waist_rise: metafield(namespace: "custom", key: "waist_rise") { value }
    bra_wiring_filter: metafield(namespace: "custom", key: "bra_wiring_filter") { value }
    back_style_filter: metafield(namespace: "custom", key: "back_style_filter") { value }
    straps_filter: metafield(namespace: "custom", key: "straps_filter") { value }
    bra_front_coverage_filter: metafield(namespace: "custom", key: "_bra_front_coverage_filter") { value }
    top_rear_coverage_filter: metafield(namespace: "custom", key: "top_rear_coverage_filter") { value }
    rear_coverage_Filter: metafield(namespace: "custom", key: "rear_coverage_Filter") { value }
    filter_colour: metafield(namespace: "custom", key: "filter_colour") { value }
    bestseller_all_applicable_filter_values: metafield(namespace: "custom", key: "bestseller_all_applicable_filter_values") { value }
  }`;
  return customProductQueryField;
}

function buildCollectionQuery(collectionHandle, cursor = null, filters = [], sortKey = null, reverse = false) {
  const afterParam = cursor ? `, after: "${cursor}"` : '';
  const filtersParam = filters.length > 0 ? `, filters: [${filters.map(f => JSON.stringify(f).replaceAll(/"([^"]+)":/g, '$1:')).join(', ')}]` : '';
  const sortKeyParam = sortKey ? `, sortKey: ${sortKey}` : '';
  const reverseParam = sortKey && reverse ? `, reverse: true` : '';

  const customProductQueryFields = state.firstLoad ? `
    bestsellers: metafield(namespace: "plp", key: "bestselling_products") {
      value
      references(first: 50) {
        nodes {
          ... on Product ${getUpdatedproductQueryFields()}
        }
      }
    }
    custom7thAnd10thProducts: metafield(namespace: "custom", key: "seventh_and_tenth_products") {
      value
      references(first: 50) {
        nodes {
          ... on Product ${getUpdatedproductQueryFields()}
        }
      }
    }
  ` : '';

  return `query CollectionByHandle {
    collectionByHandle(handle: "${collectionHandle}") {
      ${customProductQueryFields}
      products(first: ${config.productsPerPage}${afterParam}${filtersParam}${sortKeyParam}${reverseParam}) {
        edges {
          node ${getUpdatedproductQueryFields()}
        }
        filters {
          id
          label
          type
          values {
            count
            id
            input
            label
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
      }
      title
    }
  }`;
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ API Functions                                                           │
  └─────────────────────────────────────────────────────────────────────────┘
*/
async function fetchCollectionData(cursor = null, filters = [], sortKey = null, reverse = false) {
  const query = buildCollectionQuery(config.collectionHandle, cursor, filters, sortKey, reverse);

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': window.colorsToExploreAndWishlistMoveToBagSfToken
    },
    body: JSON.stringify({ query })
  };

  try {
    const response = await fetch('/api/2023-04/graphql.json', requestOptions);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (data.errors) {
      console.error('GraphQL errors:', data.errors);
      throw new Error('GraphQL query failed');
    }

    const collection = data.data.collectionByHandle;
    const products = collection.products.edges.map(edge => edge.node);
    const bestsellers = data.data?.collectionByHandle?.bestsellers?.references ? data.data.collectionByHandle.bestsellers.references.nodes : [];
    const custom7thAnd10thProducts = data.data?.collectionByHandle?.custom7thAnd10thProducts?.references ? data.data.collectionByHandle.custom7thAnd10thProducts.references.nodes : [];
    const filtersData = collection.products.filters;
    const pageInfo = collection.products.pageInfo;

    return { products, filters: filtersData, pageInfo, title: collection.title, bestsellers, custom7thAnd10thProducts };
  } catch (error) {
    console.error('Error fetching collection data:', error);
    throw error;
  }
}

/**
 * Get metafield values as an array of strings for filtering.
 * Handles both scalar values and JSON-encoded arrays.
 * @param {{ value: string } | null | undefined} metafield
 * @returns {string[]} array of values (may be empty)
 */
function getMetafieldValuesForFiltering(metafield) {
  if (!metafield || metafield.value == null) return [];

  try {
    const parsed = JSON.parse(metafield.value);
    if (Array.isArray(parsed)) {
      return parsed.map(v => String(v));
    }
    return [String(parsed)];
  } catch {
    return [String(metafield.value)];
  }
}

/**
 * Extract numeric product ID from Shopify GID format.
 * Handles both "gid://shopify/Product/123456" and numeric string "123456".
 * @param {string} productId - Product ID in GID format or numeric string
 * @returns {string} Numeric product ID, or empty string if invalid
 */
function extractProductId(productId) {
  if (!productId) return '';

  // If it's already a numeric string, return it
  if (/^\d+$/.test(String(productId).trim())) {
    return String(productId).trim();
  }

  // Extract numeric ID from GID format: gid://shopify/Product/123456
  const match = String(productId).match(/gid:\/\/shopify\/Product\/(\d+)/);
  return match ? match[1] : '';
}

/**
 * Remove products from an array based on a comma-separated string of product IDs.
 * Products can be in the format { id: "gid://shopify/Product/123" } or { node: { id: "gid://shopify/Product/123" } }.
 *
 * @param {Array} products - Array of product objects (may have 'node' wrapper)
 * @param {string} idsToRemove - Comma-separated string of numeric product IDs to remove
 * @returns {Array} New array with specified products removed
 *
 * @example
 * removeProductsByIds([{id: "gid://shopify/Product/123"}], "123,456")
 * Returns: [] (product 123 was removed)
 *
 * @example
 * removeProductsByIds([{node: {id: "gid://shopify/Product/123"}}], "456,789")
 * Returns: [{node: {id: "gid://shopify/Product/123"}}] (no match, product kept)
 */
function removeProductsByIds(products, idsToRemove) {
  if (!Array.isArray(products) || products.length === 0) {
    return [];
  }

  if (!idsToRemove || typeof idsToRemove !== 'string' || idsToRemove.trim() === '') {
    return products;
  }

  // Parse comma-separated IDs and create a Set for O(1) lookup
  const idsToRemoveSet = new Set(
    idsToRemove.split(',').map(id => String(id).trim()).filter(Boolean)
  );

  if (idsToRemoveSet.size === 0) {
    return products;
  }

  // Filter out products whose IDs match any in the set
  return products.filter(product => {
    // Handle both direct product objects and objects wrapped in 'node'
    const productObj = product.node || product;

    if (!productObj || !productObj.id) {
      // If no ID, keep the product (safer to keep than remove)
      return true;
    }

    const productId = extractProductId(productObj.id);

    // Remove product if its ID is in the set
    return !idsToRemoveSet.has(productId);
  });
}

/**
 * Get the product with matching color variant when color filter is active.
 * If a color filter is applied, finds the matching color variant from the product's
 * color_variant.references.nodes array based on colour_family.value.
 *
 * This mirrors the Liquid template logic that switches to the matching color variant
 * when a color filter is applied.
 *
 * @param {Object} product - Product object with color_variant metafield
 * @returns {Object} Product object (either the matching variant or original product)
 */
function getProductWithMatchingColorVariant(product) {
  // Check if color filter is active
  const colorFilterId = 'filter.p.m.custom.filter_colour';
  const activeColorFilters = state.activeFilters[colorFilterId];

  if (!activeColorFilters || !Array.isArray(activeColorFilters) || activeColorFilters.length === 0) {
    // No color filter active, return original product
    return product;
  }

  // Check if product has filter_colour metafield that contains any of the active color filters
  const productFilterColour = product.filter_colour?.value;
  if (!productFilterColour) {
    // No filter_colour metafield, don't render when color filter is active
    return null;
  }

  // Parse filter_colour value (could be JSON array or string)
  let productFilterColourValues = [];
  try {
    const parsed = JSON.parse(productFilterColour);
    productFilterColourValues = Array.isArray(parsed) ? parsed : [String(parsed)];
  } catch {
    productFilterColourValues = [String(productFilterColour)];
  }

  // Check if any active color filter matches the product's filter_colour
  const hasMatchingColor = activeColorFilters.some(filterColor =>
    productFilterColourValues.some(productColor =>
      String(productColor).trim().toLowerCase() === String(filterColor).trim().toLowerCase()
    )
  );

  if (!hasMatchingColor) {
    // Product doesn't have matching color, don't render when color filter is active
    return null;
  }

  // Product has matching color, now find the specific variant
  const colorVariants = product.color_variant?.references?.nodes || [];

  if (!colorVariants || colorVariants.length === 0) {
    // No color variants available, don't render when color filter is active
    return null;
  }

  // Find the first variant that matches any of the active color filters AND is availableForSale
  for (const variant of colorVariants) {
    const variantColourFamily = variant.colour_family?.value;
    if (!variantColourFamily) continue;

    // Check if this variant's colour_family matches any active color filter
    const variantMatches = activeColorFilters.some(filterColor =>
      String(variantColourFamily).trim().toLowerCase() === String(filterColor).trim().toLowerCase()
    );

    if (variantMatches) {
      // Check if variant is available for sale
      if (variant.availableForSale === true) {
        // Found matching variant that is available, merge it with original product to ensure all fields are available
        // Preserve the original product's color_variant structure for the badge
        return {
          ...product, // Start with original product to preserve all fields
          ...variant, // Override with variant-specific data (id, handle, title, images, etc.)
          // Keep the original color_variant metafield so the badge count still works
          color_variant: product.color_variant
        };
      }
      // Variant matches but is not availableForSale, continue searching for another match
    }
  }

  // No matching variant found or no available variants, don't render when color filter is active
  return null;
}

/**
 * Filter an array of products on the client side using the current
 * state.filters, state.activeFilters and state.priceRange.
 *s
 * This is intended for curated product lists like state.bestsellers
 * and state.custom7thAnd10thProducts that are not filtered via GraphQL.
 *
 * @param {Array} products - Array of product objects
 * @returns {Array} Filtered product objects
 */
function filterProductsOnClientSide(products) {
  if (!Array.isArray(products) || products.length === 0) {
    return [];
  }

  const hasPriceFilter = state.priceRange &&
    (state.priceRange.currentMin > state.priceRange.min ||
     state.priceRange.currentMax < state.priceRange.max);

  // If nothing is selected, return the original list
  if (!hasActiveFilters() && !hasPriceFilter) {
    return products;
  }

  return products.filter(product => {
    // 1) Price filter (same logic as collection products)
    if (hasPriceFilter) {
      const minPrice = Number.parseFloat(product.priceRange?.minVariantPrice?.amount || 0);
      const maxPrice = Number.parseFloat(product.priceRange?.maxVariantPrice?.amount || 0);

      if (minPrice < state.priceRange.currentMin || maxPrice > state.priceRange.currentMax) {
        return false;
      }
    }

    if (!hasActiveFilters()) {
      // Only price filter was active
      return true;
    }

    // 2) Apply each active filter
    for (const [filterId, selectedValues] of Object.entries(state.activeFilters)) {
      if (!selectedValues || selectedValues.length === 0) continue;

      // Product type filter uses the top-level productType field
      if (filterId === 'filter.p.m.custom.product_type') {
        const productType = product.productType || '';
        if (!selectedValues.includes(productType)) {
          return false;
        }
        continue;
      }

      // Size filter is based on variant options
      if (filterId === 'filter.v.option.size') {
        const productSizes = product.options?.find(opt => opt.name === 'Size')?.values || [];
        const hasMatchingSize = selectedValues.some(size => productSizes.includes(size));
        if (!hasMatchingSize) {
          return false;
        }
        continue;
      }

      // All metafield-based filters in the "custom" namespace
      if (filterId.startsWith('filter.p.m.custom.')) {
        const metafieldKey = filterId.replace('filter.p.m.custom.', '');

        // colour filter and the rest are all encoded as metafields on the product
        const metafield = product[metafieldKey];
        const metafieldValues = getMetafieldValuesForFiltering(metafield);

        // Require at least one overlap between selected filter values and metafield values
        const hasMatch = metafieldValues.some(v => selectedValues.includes(v));
        if (!hasMatch) {
          return false;
        }

        continue;
      }

      // For any other filter ids (e.g. namespaces we don't have data for on these products),
      // we skip client-side checks so they don't accidentally exclude all curated products.
    }

    return true;
  });
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Filter Drawer Functions                                                 │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function closeFilterDrawer() {
  const filterDrawer = document.querySelector('.neo-filter-drawer');
  if (filterDrawer) {
    filterDrawer.classList.remove('active');
    enableBodyScroll();
  }
}

function openFilterDrawer() {
  const filterDrawer = document.querySelector('.neo-filter-drawer');
  if (filterDrawer) {
    filterDrawer.classList.add('active');
    disableBodyScroll();
    closeSortDropDown();
  }
}

function disableBodyScroll() {
  document.body.style.overflow = 'hidden';
}

function enableBodyScroll() {
  document.body.style.overflow = null;
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Sort Dropdown Functions                                                 │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function toggleSortDropDown(event) {
  event.preventDefault();
  event.stopPropagation();
  const sortDropdownContainer = event.currentTarget.closest('[data-id="desktop-sort-container"]');
  sortDropdownContainer.classList.toggle('active');
}

function openSortDropDown() {
  sortDropdownContainer.classList.add('active');
}

function closeSortDropDown() {
  sortDropdownContainer.classList.remove('active');
}

function toggleMobileSortDropDown() {
  mobileSortDropdownContainer.classList.toggle('active');
}

function openMobileSortDropDown() {
  mobileSortDropdownContainer.classList.add('active');
}

function closeMobileSortDropDown() {
  mobileSortDropdownContainer.classList.remove('active');
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Mobile Sort Drawer Drag-to-Close Functionality                          │
  └─────────────────────────────────────────────────────────────────────────┘
*/
(function initMobileSortDrawerDrag() {
  if (!mobileSortDropdownContainer) return;

  const drawerContent = mobileSortDropdownContainer.querySelector('.mobile-sort-dropdown-container-content');
  const dragLine = mobileSortDropdownContainer.querySelector('.line');

  if (!drawerContent || !dragLine) return;

  let isDragging = false;
  let startY = 0;
  let currentY = 0;
  let initialTransform = 0;
  let drawerHeight = 0;
  let animationFrameId = null;

  // Threshold to close drawer (30% of drawer height)
  const CLOSE_THRESHOLD = 0.25;
  // Minimum velocity (px/ms) to trigger close on release
  const VELOCITY_THRESHOLD = 0.2;
  let lastY = 0;
  let lastTime = 0;
  let velocity = 0;

  function updateTransform() {
    if (!isDragging) return;

    const dragDistance = currentY - startY;
    // Only allow downward dragging (positive values)
    const clampedDistance = Math.max(0, dragDistance);

    // Calculate percentage: 0% = fully open, 100% = fully closed
    const dragPercentage = clampedDistance / drawerHeight;

    // Apply transform: translateY(0%) when open, translateY(100%) when closed
    const translateY = Math.min(100, dragPercentage * 100);

    drawerContent.style.transform = `translateY(${translateY}%)`;

    // Update overlay opacity based on drag
    const overlay = mobileSortDropdownContainer.querySelector('.overlay');
    if (overlay) {
      const opacity = Math.max(0, 1 - dragPercentage);
      overlay.style.opacity = opacity.toString();
    }

    // Calculate velocity for momentum-based closing
    const now = performance.now();
    if (lastTime > 0) {
      const timeDelta = now - lastTime;
      const yDelta = currentY - lastY;
      velocity = timeDelta > 0 ? yDelta / timeDelta : 0;
    }
    lastY = currentY;
    lastTime = now;

    animationFrameId = requestAnimationFrame(updateTransform);
  }

  function handleTouchStart(e) {
    if (!mobileSortDropdownContainer.classList.contains('active')) return;

    const touch = e.touches[0];
    startY = touch.clientY;
    currentY = startY;
    lastY = startY;
    lastTime = performance.now();
    velocity = 0;

    // Get drawer height
    drawerHeight = drawerContent.offsetHeight;

    // Disable transition during drag for smooth interaction
    drawerContent.style.transition = 'none';
    const overlay = mobileSortDropdownContainer.querySelector('.overlay');
    if (overlay) {
      overlay.style.transition = 'none';
    }

    isDragging = true;
    initialTransform = 0;

    // Prevent default scrolling
    e.preventDefault();

    updateTransform();
  }

  function handleTouchMove(e) {
    if (!isDragging) return;

    const touch = e.touches[0];
    currentY = touch.clientY;

    // Prevent default scrolling
    e.preventDefault();
  }

  function handleTouchEnd(e) {
    if (!isDragging) return;

    isDragging = false;

    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    const dragDistance = currentY - startY;
    const dragPercentage = dragDistance / drawerHeight;

    // Re-enable transitions
    drawerContent.style.transition = '';
    const overlay = mobileSortDropdownContainer.querySelector('.overlay');
    if (overlay) {
      overlay.style.transition = '';
    }

    // Determine if should close based on distance or velocity
    const shouldClose = dragPercentage > CLOSE_THRESHOLD || velocity > VELOCITY_THRESHOLD;

    if (shouldClose) {
      closeMobileSortDropDown();
      // Reset transform after transition completes
      setTimeout(() => {
        drawerContent.style.transform = '';
        if (overlay) overlay.style.opacity = '';
      }, 450); // Match CSS transition duration
    } else {
      // Snap back to open position
      drawerContent.style.transform = 'translateY(0%)';
      if (overlay) overlay.style.opacity = '1';
    }

    // Reset velocity tracking
    lastY = 0;
    lastTime = 0;
    velocity = 0;
  }

  function handleTouchCancel(e) {
    handleTouchEnd(e);
  }

  // Attach event listeners to the drag line
  dragLine.addEventListener('touchstart', handleTouchStart, { passive: false });
  dragLine.addEventListener('touchmove', handleTouchMove, { passive: false });
  dragLine.addEventListener('touchend', handleTouchEnd, { passive: true });
  dragLine.addEventListener('touchcancel', handleTouchCancel, { passive: true });
})();


function applySortDesktop(event) {
  let sortValue = event.currentTarget.dataset.value;
  let sortTitle = event.currentTarget.dataset.title;
  const sortDropdownContainer = document.querySelector('[data-id="desktop-sort-container"]');
  const sortButtonSpan = document.querySelector('[data-id="desktop-sort-container"] [data-id="desktop-sort-button-text"]');
  sortButtonSpan.innerText = sortTitle;
  sortDropdownContainer.classList.remove('active');
  applySort(sortValue, sortTitle);
}

function applySortMobile(event) {
  let sortValue = event.currentTarget.dataset.value;
  let sortTitle = event.currentTarget.dataset.title;
  const mobileSortDropdownContainer = document.querySelector('[data-id="mobile-sort-dropdown-container"]');
  mobileSortDropdownContainer.classList.remove('active');
  applySort(sortValue, sortTitle);
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Make functions globally available                                       │
  └─────────────────────────────────────────────────────────────────────────┘
*/
globalThis.closeFilterDrawer = closeFilterDrawer;
globalThis.openFilterDrawer = openFilterDrawer;

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Quick Filters Rendering                                                 │
  └─────────────────────────────────────────────────────────────────────────┘
*/
function renderQuickFilters(filters) {
  const quickFiltersContainer = document.querySelector('.quick-filters-container .swiper-wrapper');
  const quickFiltersSkeletonContainer = document.querySelector('.quick-filters-container .swiper-wrapper');
  if (!quickFiltersContainer || !quickFiltersSkeletonContainer) return;

  // Parse quick filter values from config
  const quickFilterValues = config.quickFilterValues
    ? config.quickFilterValues.split(',').map(v => v.trim()).filter(Boolean)
    : [];

  if (quickFilterValues.length === 0) {
    // Remove all skeletons if no quick filters configured
    quickFiltersContainer.classList.add('hidden');
    quickFiltersSkeletonContainer.querySelectorAll('.swiper-slide').forEach(slide => slide.remove());
    return;
  }

  quickFiltersContainer.classList.remove('hidden');

  // Find matching filters from GraphQL response
  // Order follows GraphQL filters order, not quickFilterValues order
  const quickFilterItems = [];
  const quickFilterValuesSet = new Set(quickFilterValues.map(v => v.toLowerCase()));

  // Iterate through GraphQL filters to maintain their order
  filters.forEach(filter => {
    // Check each value in this filter
    filter.values.forEach(value => {
      // Check if this value is in the quickFilterValues list
      const isQuickFilter = quickFilterValuesSet.has(value.label.toLowerCase());

      if (isQuickFilter) {
        quickFilterItems.push({
          filterId: filter.id,
          filterValue: value.label,
          count: value.count,
          isDisabled: value.count === 0
        });
      }
    });
  });

  // Remove all existing content (skeletons and example item)
  quickFiltersSkeletonContainer.innerHTML = '';

  // Render quick filter items
  quickFilterItems.forEach(item => {
    const activeClass = state.activeFilters[item.filterId]?.includes(item.filterValue) ? 'active' : '';
    const disabledClass = item.isDisabled ? 'disabled' : '';

    const slide = document.createElement('div');
    slide.className = 'swiper-slide';
    slide.innerHTML = `
      <span
        class="quick-filter-item ${activeClass} ${disabledClass}"
        data-filter-id="${item.filterId}"
        data-filter-value="${item.filterValue}"
        ${item.isDisabled ? 'data-disabled="true"' : ''}
      >
        ${item.filterValue}
        <svg class="close-icon" xmlns="http://www.w3.org/2000/svg" width="10" height="11" viewBox="0 0 10 11" fill="none">
          <path d="M0.228129 0.728129C0.532301 0.423957 1.0253 0.423957 1.32931 0.728129L5 4.39882L8.67069 0.728129C8.97486 0.423957 9.46786 0.423957 9.77187 0.728129C10.076 1.0323 10.076 1.5253 9.77187 1.82931L6.10118 5.5L9.77187 9.17069C10.076 9.47486 10.076 9.96786 9.77187 10.2719C9.4677 10.576 8.9747 10.5759 8.67069 10.2719L5 6.60118L1.32931 10.2719C1.02514 10.5759 0.532139 10.5759 0.228129 10.2719C-0.076043 9.9677 -0.076043 9.4747 0.228129 9.17069L3.89882 5.5L0.228129 1.82931C-0.076043 1.52514 -0.076043 1.03214 0.228129 0.728129Z" fill="black"></path>
        </svg>
      </span>
    `;
    quickFiltersSkeletonContainer.appendChild(slide);
  });

  // Initialize quick filter clicks
  initQuickFilterClicks();
  if (!isMobile) {
    initQuickFiltersSwiper();
  }
}

function initQuickFilterClicks() {
  document.querySelectorAll('.quick-filter-item[data-filter-id]').forEach(item => {
    // Remove existing listeners by cloning
    const newItem = item.cloneNode(true);
    item.parentNode.replaceChild(newItem, item);

    newItem.addEventListener('click', (e) => {
      // Prevent clicks on disabled items
      if (e.currentTarget.dataset.disabled === 'true') {
        return;
      }

      const filterId = e.currentTarget.dataset.filterId;
      const filterValue = e.currentTarget.dataset.filterValue;

      if (!filterId || !filterValue) return;

      // Ensure the activeFilters object has an array for the clicked filter.
      // This is necessary to safely push new filter values or check for existence.
      // Without this, operations like push/indexOf would fail if the array was undefined.
      if (!state.activeFilters[filterId]) {
        state.activeFilters[filterId] = [];
      }

      const valueIndex = state.activeFilters[filterId].indexOf(filterValue);
      if (valueIndex === -1) {
        // Add filter
        state.activeFilters[filterId].push(filterValue);
        newItem.classList.add('active');
      } else {
        // Remove filter
        state.activeFilters[filterId].splice(valueIndex, 1);
        newItem.classList.remove('active');
      }

      // Remove empty filter arrays
      if (state.activeFilters[filterId].length === 0) {
        delete state.activeFilters[filterId];
      }

      // Update filter drawer UI to reflect the change
      updateFilterDrawerUI();

      // Apply filters
      applyFilters('quickFilter', { quick_filter_value: filterValue });
    });
  });
}

function initQuickFiltersSwiper() {
  let quickFiltersSwiperOptions = {
    slidesPerView: 7,
    spaceBetween: 20,
    breakpoints: {
      0: {
        slidesPerView: 2.5,
        spaceBetween: 20
      },
      800: {
        slidesPerView: 4,
        spaceBetween: 20
      },
      1100: {
        slidesPerView: 5,
        spaceBetween: 20
      },
      1550: {
        slidesPerView: config.templateSuffix === 'new-arrivals' ? 6 : 7,
        spaceBetween:20
      }
    },
    navigation: {
      nextEl: ".quick-filters-container .quick-filters-navigation.next",
      prevEl: ".quick-filters-container .quick-filters-navigation.prev",
    },
  };

  if (document.readyState === "loading") {
    document.addEventListener('DOMContentLoaded', () => {
      globalThis.quickFiltersSwiper = new Swiper('.quick-filters-container .swiper', quickFiltersSwiperOptions);
    }, { once: true });
  } else {
    globalThis.quickFiltersSwiper = new Swiper('.quick-filters-container .swiper', quickFiltersSwiperOptions);
  }
}

function updateFilterValueUI(allFilters) {
  // Update active state in filter drawer to match state.activeFilters
  allFilters.forEach(item => {
    const filterId = item.dataset.filterId;
    const filterValue = item.dataset.filterValue;

    if (state.activeFilters[filterId]?.includes(filterValue)) {
      item.classList.add('active');
    } else {
      item.classList.remove('active');
    }
  });
}

function updateFilterDrawerUI() {
  // Update active state in filter drawer to match state.activeFilters
  updateFilterValueUI(document.querySelectorAll('.neo-filter-value-item[data-filter-id]'));
  // Also update in-grid size filter
  updateInGridSizeFilterUI();
}

function updateQuickFiltersUI() {
  // Update active state in quick filters to match state.activeFilters
  updateFilterValueUI(document.querySelectorAll('.quick-filter-item[data-filter-id]'));
}

function updateInGridSizeFilterUI() {
  // Update active state in in-grid size filter to match state.activeFilters
  const sizeFilterId = 'filter.v.option.size';
  const inGridSizeItems = document.querySelectorAll('.in-grid-size-filters .filter-grid-item[data-value]');

  inGridSizeItems.forEach(item => {
    const filterValue = item.dataset.value;
    if (state.activeFilters[sizeFilterId]?.includes(filterValue)) {
      item.classList.add('active');
    } else {
      item.classList.remove('active');
    }
  });
}

/**
 * Generate HTML for in-grid size filter
 * @param {Object} sizeFilter - The size filter object from GraphQL
 * @returns {string} HTML string for the in-grid size filter
 */
function generateInGridSizeFilterHTML(sizeFilter) {
  if (!sizeFilter || !config.inGridSizeFilter?.show) {
    return '';
  }

  const sizeFilterId = 'filter.v.option.size';
  const filterValues = sizeFilter.values || [];

  // Generate filter grid items
  const filterItemsHTML = filterValues.map(value => {
    const isActive = state.activeFilters[sizeFilterId]?.includes(value.label) ? 'active' : '';
    const isDisabled = value.count === 0 ? 'disabled' : '';

    return `
      <span
        class="filter-grid-item fs-16 fw-400 flex items-center justify-center ${isActive} ${isDisabled}"
        data-value="${value.label}"
        data-filter-id="${sizeFilterId}"
        ${isDisabled ? 'data-disabled="true"' : ''}
        onclick="applyInGridSizeFilter(event)"
      >${value.label}</span>
    `;
  }).join('');

  // Calculate grid columns and rows based on filter count
  const totalFilters = filterValues.length;
  const columns = 9; // As per the HTML template
  const rows = Math.ceil(totalFilters / columns);

  return `
    <li class="grid__item grid__item--full-width show-on-mobile">
      <div class="in-grid-size-filters">
        <div class="in-grid-size-filters-heading-container flex items-center justify-center gap-1">
          <span class="size-icon">${config.inGridSizeFilter.svg || ''}</span>
          <h3 class="fw-600 fs-20">${config.inGridSizeFilter.heading || 'Size'}</h3>
        </div>
        <div class="in-grid-size-filters-container">
          <p class="fs-14 fw-400">${config.inGridSizeFilter.sub_title || ''}</p>
          <div class="facets-container">
            <div class="filters-grid hide-scrollbar" style="--columns: ${columns}; --rows: ${rows};">
              ${filterItemsHTML}
            </div>
          </div>
        </div>
      </div>
    </li>
  `;
}

/**
 * Apply size filter from in-grid filter
 * @param {Event} event - Click event
 */
function applyInGridSizeFilter(event) {
  const item = event.currentTarget;

  // Prevent clicks on disabled items
  if (item.dataset.disabled === 'true') {
    return;
  }

  const filterId = item.dataset.filterId;
  const filterValue = item.dataset.value;

  if (!filterId || !filterValue) return;

  // Ensure the activeFilters object has an array for the clicked filter
  if (!state.activeFilters[filterId]) {
    state.activeFilters[filterId] = [];
  }

  const valueIndex = state.activeFilters[filterId].indexOf(filterValue);
  if (valueIndex === -1) {
    // Add filter
    state.activeFilters[filterId].push(filterValue);
    item.classList.add('active');
  } else {
    // Remove filter
    state.activeFilters[filterId].splice(valueIndex, 1);
    item.classList.remove('active');
  }

  // Remove empty filter arrays
  if (state.activeFilters[filterId].length === 0) {
    delete state.activeFilters[filterId];
  }

  // Update filter drawer UI to reflect the change (this will also update in-grid filter)
  updateFilterDrawerUI();

  // Update quick filters UI
  updateQuickFiltersUI();

  // Apply filters
  applyFilters();
}

// Make function globally available
globalThis.applyInGridSizeFilter = applyInGridSizeFilter;

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Filter Rendering                                                        │
  └─────────────────────────────────────────────────────────────────────────┘
*/
function getFilterImageUrl(filterLabel, filterType, filterName) {
  // IMPLEMENTED: Resolve only against the Liquid-provided map. JavaScript must
  // not construct /cdn/shop/files URLs because those URLs can become stale.
  // No images for Jockey Collection and Size filters
  if (filterName === 'Jockey Collection' || filterName === 'Size') {
    return null;
  }

  // Build label slug matching Liquid logic:
  // strip | downcase | replace: ' ', '-' | remove: '#' | replace: '&', 'n' | replace: '%', '_25' | replace: ',', '_'
  let labelSlug = filterLabel
    .trim()
    .toLowerCase()
    .replaceAll(/\s+/g, '-')
    .replaceAll('#', '')
    .replaceAll('&', 'n')
    .replaceAll('%', '_25')
    .replaceAll(',', '_')
    .replaceAll(/[^a-z0-9-_]/g, ''); // Remove any other special chars

  // The filter name is part of the lookup so Product Type can safely use its
  // gender-specific filename even when another filter has the same label.
  const mappedUrl = (config.filterImageUrls || []).find((entry) => entry.filter === filterName && entry.key === labelSlug)?.url;
  if (!mappedUrl) return null;

  // For Product Type filter, append gender suffix
  if (filterName === 'Product Type') {
    const gender = config.collectionGender || 'default';
    return mappedUrl;
  }

  // For all other filters (except Jockey Collection and Size), use simple format
  return mappedUrl;
}

function renderStandardFilter(filter, isFirst = false) {
  const filterId = filter.id.replaceAll('.', '-');
  const activeClass = isFirst ? 'active' : '';

  let valuesHTML = filter.values.map(value => {
    const isActive = state.activeFilters[filter.id]?.includes(value.label) ? 'active' : '';
    const isDisabled = value.count === 0 ? 'disabled' : '';
    const imageUrl = getFilterImageUrl(value.label, 'standard', filter.label);

    // Only show image if URL is not null (excludes Jockey Collection and Size)
    const imageHTML = imageUrl
      ? `<img class="neo-filter-value-image"
            src="${imageUrl}"
            loading="lazy"
            width="auto"
            height="auto"
            alt="${value.label}"
            onerror="this.style.display='none'">`
      : '';

    return `
      <li class="neo-filter-value-item ${isActive} ${isDisabled}" data-filter-id="${filter.id}" data-filter-value="${value.label}" ${isDisabled ? 'data-disabled="true"' : ''}>
        <label class="neo-filter-value-item-label ${isDisabled}">
          ${imageHTML}
          <div class="neo-filter-value-label-container row flex">
            <div class="relative">
              <svg class="check-box-rec absolute" width="1.1rem" height="1.1rem" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <rect width="16" height="16" stroke="currentColor" fill="none" stroke-width="1"></rect>
              </svg>
              <svg class="icon icon-checkmark absolute" aria-hidden="true" width="1.1rem" height="1.1rem" viewBox="0 0 11 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5 3.5L2.83333 4.75L4.16667 6L9.5 1" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="flex column">
              <p class="neo-filter-value-label fw-600 fs-14">${value.label}</p>
              <p class="neo-filter-value-count fw-400 fs-10">${value.count} Products</p>
            </div>
          </div>
        </label>
      </li>
    `;
  }).join('');

  return `
    <div class="neo-filter-tab ${activeClass} flex row items-center justify-between no-wrap" data-filter-id="${filterId}">
      <span class="neo-filter-label fw-500 fs-16">${filter.label}</span>
      <svg class="arrow-down" width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1.30469L7.24 7.30468L14 1.30469" stroke="#3E3E3E" stroke-width="2" stroke-linecap="round"></path>
      </svg>
    </div>
    <div class="neo-filter-values-container ${activeClass}">
      <ul class="neo-filter-value-items p-0 m-0">
        ${valuesHTML}
      </ul>
    </div>
  `;
}

function renderColourFilter(filter, isOpen = false) {
  const filterId = filter.id.replaceAll('.', '-');
  const activeClass = isOpen ? 'active' : '';

  let valuesHTML = filter.values.map(value => {
    const isActive = state.activeFilters[filter.id]?.includes(value.label) ? 'active' : '';
    const isDisabled = value.count === 0 ? 'disabled' : '';
    // IMPLEMENTED: Preserve Shopify/API swatches first; custom filter images
    // are only the fallback for values without Shopify-provided swatch data.
    const imageUrl = value.swatch?.image || value.swatch?.image_url || value.swatch?.url || getFilterImageUrl(value.label, 'colour', filter.label);

    return `
      <li class="neo-filter-value-item ${isActive} ${isDisabled}" data-filter-id="${filter.id}" data-filter-value="${value.label}" ${isDisabled ? 'data-disabled="true"' : ''}>
        <label class="neo-filter-value-item-label-colour flex row no-wrap items-start justify-start ${isDisabled}">
          ${imageUrl ? `<img class="neo-filter-value-image-colour"
            src="${imageUrl}"
            loading="lazy"
            width="auto"
            height="auto"
            alt="${value.label}"
            onerror="this.style.display='none'">` : ''}
          <div class="neo-filter-value-label-container-colour flex row wrap items-center">
            <p class="neo-filter-value-label fw-600 fs-18">${value.label}</p>
            <p class="neo-filter-value-count fw-400 fs-10">(${value.count})</p>
          </div>
        </label>
      </li>
    `;
  }).join('');

  return `
    <div class="neo-filter-tab ${activeClass} flex row items-center justify-between no-wrap" data-filter-id="${filterId}">
      <span class="neo-filter-label fw-500 fs-16">${filter.label}</span>
      <svg class="arrow-down" width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1.30469L7.24 7.30468L14 1.30469" stroke="#3E3E3E" stroke-width="2" stroke-linecap="round"></path>
      </svg>
    </div>
    <div class="neo-filter-values-container ${activeClass}">
      <p class="neo-filter-description fw-400 fs-12 mb-20">Select your favourite colors:</p>
      <ul class="neo-filter-value-items p-0 m-0">
        ${valuesHTML}
      </ul>
    </div>
  `;
}

function renderPriceFilter(filter, isOpen = false) {
  const filterId = filter.id.replaceAll('.', '-');
  const activeClass = isOpen ? 'active' : '';
  // Parse price range from filter values
  const priceData = filter.values[0];
  if (!priceData) return '';
  try {
    const inputData = JSON.parse(priceData.input);
    const minPrice = state.priceRange.min == null ? inputData.price?.min : state.priceRange.min;
    const maxPrice = state.priceRange.max == null ? inputData.price?.max : state.priceRange.max;

    state.priceRange = {
      min: minPrice,
      max: maxPrice,
      currentMin: state.priceRange.currentMin || minPrice,
      currentMax: state.priceRange.currentMax || maxPrice
    };

    return `
      <div class="neo-filter-tab ${activeClass} flex row items-center justify-between no-wrap" data-filter-id="${filterId}">
        <span class="neo-filter-label fw-500 fs-16">Price</span>
        <svg class="arrow-down" width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1.30469L7.24 7.30468L14 1.30469" stroke="#3E3E3E" stroke-width="2" stroke-linecap="round"></path>
        </svg>
      </div>
      <div class="neo-filter-values-container ${activeClass}">
        <p class="fw-400 fs-12 mb-20 color-grey">Select a price range:</p>
        <div class="neo-filter-price-range-slider">
          <div class="neo-filter-price-range-slider-inputs-container">
            <span class="neo-filter-price-range-slider-input-value fw-600 fs-14" id="price-min-display">₹${Math.floor(state.priceRange.currentMin)}</span>
            <span class="neo-filter-price-range-slider-input-value fw-600 fs-14" id="price-max-display">₹${Math.floor(state.priceRange.currentMax)}</span>

            <div class="neo-filter-price-range-slider-progress" style="--right: 1%; --left: 1%;"></div>

            <input type="range" class="neo-filter-price-range-slider-input" id="price-range-min" min="${minPrice}" max="${maxPrice}" value="${state.priceRange.currentMin}">
            <input type="range" class="neo-filter-price-range-slider-input max" id="price-range-max" min="${minPrice}" max="${maxPrice}" value="${state.priceRange.currentMax}">
          </div>
        </div>
        <div class="neo-filter-price-images-container flex no-wrap row justify-center">
          ${generatePriceFilterImages(minPrice, maxPrice)}
        </div>
      </div>
    `;
  } catch (e) {
    console.error('Error parsing price filter:', e);
    return '';
  }
}

/**
 * Generates price filter images HTML based on config
 * Adds disabled class if price is outside collection min/max range
 */
function generatePriceFilterImages(minPrice, maxPrice) {
  if (!config.priceFilterImages || config.priceFilterImages.length === 0) {
    return '';
  }

  return config.priceFilterImages.map(item => {
    const priceValue = Number.parseFloat(item.value);
    const isDisabled = priceValue < minPrice || priceValue > maxPrice;
    const disabledClass = isDisabled ? 'disabled' : '';
    const disabledAttr = isDisabled ? 'style="opacity: 0.5; cursor: not-allowed;"' : '';

    return `
      <img
        class="neo-price-filter-image ${disabledClass}"
        src="${item.imageUrl}"
        loading="lazy"
        width="139"
        height="80"
        alt="${priceValue}"
        data-price="${priceValue}"
        ${disabledAttr}
      >
    `;
  }).join('');
}

/**
 * Updates the disabled state of price filter images based on current price range
 */
function updatePriceFilterImagesState() {
  const priceImages = document.querySelectorAll('.neo-price-filter-image');
  if (priceImages.length === 0 || !config.priceFilterImages) return;

  const minPrice = state.priceRange.min;
  const maxPrice = state.priceRange.max;

  priceImages.forEach(img => {
    const priceValue = Number.parseFloat(img.dataset.price || img.alt);
    const isDisabled = priceValue < minPrice || priceValue > maxPrice;

    if (isDisabled) {
      img.classList.add('disabled');
      img.style.opacity = '0.5';
      img.style.cursor = 'not-allowed';
    } else {
      img.classList.remove('disabled');
      img.style.opacity = '';
      img.style.cursor = '';
    }
  });
}

function renderSizeFilter(filter, isOpen = false) {
  const filterId = filter.id.replaceAll('.', '-');
  const activeClass = isOpen ? 'active' : '';

  let valuesHTML = filter.values.map((value, index) => {
    const isActive = state.activeFilters[filter.id]?.includes(value.label) ? 'active' : '';
    const isDisabled = value.count === 0 ? 'disabled' : '';
    const hiddenClass = index >= maxVisibileSizeFilters ? 'hidden-size-item' : '';
    return `
      <li class="neo-filter-value-item flex items-center no-wrap ${isActive} ${isDisabled} ${hiddenClass}" data-filter-id="${filter.id}" data-filter-value="${value.label}" ${isDisabled ? 'data-disabled="true"' : ''}>
        <label class="neo-filter-value-label fw-600 fs-14 ${isDisabled}">${value.label}</label>
        <p class="neo-filter-value-count fw-400 fs-10">(${value.count})</p>
      </li>
    `;
  }).join('');

  const showMoreButton = filter.values.length > maxVisibileSizeFilters
    ? `<button class="neo-filter-show-more-button" onclick="toggleSizeItems(this)">+ Show more</button>`
    : '';

  return `
    <div class="neo-filter-tab ${activeClass} flex row items-center justify-between no-wrap" data-filter-id="${filterId}">
      <span class="neo-filter-label fw-500 fs-16">${filter.label}</span>
      <svg class="arrow-down" width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1.30469L7.24 7.30468L14 1.30469" stroke="#3E3E3E" stroke-width="2" stroke-linecap="round"></path>
      </svg>
    </div>
    <div class="neo-filter-values-container ${activeClass}">
      <p class="fw-400 fs-12 mb-20 color-grey">Available sizes for you:</p>
      <ul class="neo-filter-size-items flex wrap items-start p-0 m-0">
        ${valuesHTML}
      </ul>
      ${showMoreButton}
    </div>
  `;
}

function toggleSizeItems(button) {
  const container = button.previousElementSibling;
  const sizeItems = Array.from(container.querySelectorAll('.neo-filter-value-item'));
  const isExpanded = button.classList.contains('expanded');

  // maxVisibileSizeFilters must be available in scope
  // When isExpanded is true, toggle back to limited (hide those past max)
  if (isExpanded) {
    sizeItems.forEach((item, idx) => {
      if (idx >= maxVisibileSizeFilters) {
        item.classList.add('hidden-size-item');
      }
    });
  } else {
    sizeItems.forEach((item, idx) => {
      if (idx >= maxVisibileSizeFilters) {
        item.classList.remove('hidden-size-item');
      }
    });
  }

  button.classList.toggle('expanded');
  button.textContent = isExpanded ? '+ Show more' : '- Show less';

  // Find the nearest container with class .neo-filter-values-container
  const valuesContainer = container.closest('.neo-filter-values-container');
  if (valuesContainer) {
    const scrollHeight = valuesContainer.scrollHeight;
    valuesContainer.style.setProperty('--height', `${scrollHeight}px`);
  }
}

globalThis.toggleSizeItems = toggleSizeItems;

let isRenderingFiltersOnLoad = isMobile;

function renderFilters(filters) {
  const container = document.getElementById('neo-filter-accordions-container');
  if (!container) return;

  // Preserve accordion open/closed state before re-rendering
  const openAccordions = new Set();
  if (state.filters.length > 0) {
    document.querySelectorAll('.neo-filter-tab.active').forEach(tab => {
      const filterId = tab.dataset.filterId;
      if (filterId) openAccordions.add(filterId);
    });
  }

  state.filters = filters;
  let filtersHTML = '';
  let isFirst = isMobile;

  // Render filters in the order they appear in the array
  filters.forEach(filter => {
    // Skip "Style Number" filter
    if (filter.label === 'Style Number') {
      return;
    }

    // Determine filter type based on filter id
    let filterType = 'standard';
    if (filter.id === 'filter.p.m.custom.filter_colour') {
      filterType = 'colour';
    } else if (filter.id === 'filter.v.price') {
      filterType = 'price';
    } else if (filter.id === 'filter.v.option.size') {
      filterType = 'size';
    }
    // Default is 'standard' for LIST type filters

    // Check if this accordion was open before
    const accordionFilterId = filter.id.replaceAll('.', '-');
    const wasOpen = openAccordions.has(accordionFilterId);

    // Render based on filter type
    switch (filterType) {
      case 'standard':
        filtersHTML += renderStandardFilter(filter, (isFirst && !wasOpen && isRenderingFiltersOnLoad) || wasOpen);
        isFirst = false;
        isRenderingFiltersOnLoad = false;
        break;
      case 'colour':
        filtersHTML += renderColourFilter(filter, wasOpen);
        break;
      case 'price':
        filtersHTML += renderPriceFilter(filter, wasOpen);
        break;
      case 'size':
        filtersHTML += renderSizeFilter(filter, wasOpen);
        break;
    }
  });

  container.innerHTML = filtersHTML;

  // Initialize filter accordion clicks
  initFilterAccordions();
  initFilterValueClicks();
  initPriceRangeSlider();

  // Update price filter images disabled state
  updatePriceFilterImagesState();

  // Render quick filters
  renderQuickFilters(filters);

  // Update in-grid size filter UI if it exists
  updateInGridSizeFilterUI();
}

let lastOpenAccordionFilterId = null;

function initFilterAccordions() {
  function handleFilterAccordionClick(e) {
    const clickedTab = e.currentTarget;
    const valuesContainer = clickedTab.nextElementSibling;
    const clickedFilterId = clickedTab.dataset.filterId;

    // Close previously open accordion if it's different from the clicked one
    if (lastOpenAccordionFilterId && lastOpenAccordionFilterId !== clickedFilterId) {
      const previousTab = document.querySelector(`.neo-filter-tab[data-filter-id="${lastOpenAccordionFilterId}"]`);
      if (previousTab && previousTab.classList.contains('active')) {
        previousTab.classList.remove('active');
        const previousContainer = previousTab.nextElementSibling;
        if (previousContainer) {
          previousContainer.classList.remove('active');
        }
      }
    }

    // Toggle active class on clicked tab
    clickedTab.classList.toggle('active');

    // Toggle active class on values container
    valuesContainer.classList.toggle('active');

    // Set height for animation
    if (!valuesContainer.style.getPropertyValue('--height')) {
      valuesContainer.style.setProperty('--height', `${valuesContainer.scrollHeight}px`);
    }

    // Update last open accordion filter ID
    if (clickedTab.classList.contains('active')) {
      lastOpenAccordionFilterId = clickedFilterId;
    } else {
      lastOpenAccordionFilterId = null;
    }
  }

  function handleMobileFilterTabClick(e) {
    const clickedTab = e.currentTarget;
    const clickedContainer = clickedTab.nextElementSibling;
    const isCurrentlyActive = clickedTab.classList.contains('active');

    // Find all currently active tabs and containers
    const allTabs = document.querySelectorAll('.neo-filter-tab');
    const allContainers = document.querySelectorAll('.neo-filter-values-container');

    // Remove active class from all tabs and containers
    allTabs.forEach(tab => {
      tab.classList.remove('active');
    });
    allContainers.forEach(container => {
      container.classList.remove('active');
    });

    // If the clicked tab was not active, activate it
    if (!isCurrentlyActive) {
      clickedTab.classList.add('active');
      clickedContainer.classList.add('active');
    }
  }
  document.querySelectorAll('.neo-filter-tab').forEach(tab => {
    if(isMobile) {
      tab.addEventListener('click', handleMobileFilterTabClick);
    } else {
      tab.addEventListener('click', handleFilterAccordionClick);
    }
  });
}

function initFilterValueClicks() {
  document.querySelectorAll('.neo-filter-value-item[data-filter-id]').forEach(item => {
    item.addEventListener('click', (e) => {
      // Prevent clicks on disabled items
      if (e.currentTarget.dataset.disabled === 'true') {
        return;
      }

      const filterId = e.currentTarget.dataset.filterId;
      const filterValue = e.currentTarget.dataset.filterValue;

      if (!filterId || !filterValue) return;

      // Toggle active state
      e.currentTarget.classList.toggle('active');

      // Update state
      if (!state.activeFilters[filterId]) {
        state.activeFilters[filterId] = [];
      }

      const valueIndex = state.activeFilters[filterId].indexOf(filterValue);
      if (valueIndex === -1) {
        state.activeFilters[filterId].push(filterValue);
      } else {
        state.activeFilters[filterId].splice(valueIndex, 1);
      }

      // Remove empty filter arrays
      if (state.activeFilters[filterId].length === 0) {
        delete state.activeFilters[filterId];
      }

      // Update quick filters UI to reflect the change
      updateQuickFiltersUI();

      // Update in-grid size filter UI to reflect the change
      updateInGridSizeFilterUI();

      // Apply filters
      applyFilters();
    });
  });
}

function initPriceRangeSlider() {
  const minInput = document.getElementById('price-range-min');
  const maxInput = document.getElementById('price-range-max');
  const minDisplay = document.getElementById('price-min-display');
  const maxDisplay = document.getElementById('price-max-display');
  const progress = document.querySelector('.neo-filter-price-range-slider-progress');

  if (!minInput || !maxInput) return;

  function updateSlider() {
    const min = Number.parseInt(minInput.value);
    const max = Number.parseInt(maxInput.value);
    const range = Number.parseInt(maxInput.max) - Number.parseInt(minInput.min);

    const leftPercent = ((min - Number.parseInt(minInput.min)) / range) * 100;
    const rightPercent = 100 - ((max - Number.parseInt(minInput.min)) / range) * 100;

    if (progress) {
      progress.style.setProperty('--left', `${leftPercent}%`);
      progress.style.setProperty('--right', `${rightPercent}%`);
    }

    if (minDisplay) minDisplay.textContent = `₹${min}`;
    if (maxDisplay) maxDisplay.textContent = `₹${max}`;

    state.priceRange.currentMin = min;
    state.priceRange.currentMax = max;
  }

  let debounceTimer;

  minInput.addEventListener('input', () => {
    if (Number.parseInt(minInput.value) > Number.parseInt(maxInput.value) - 50) {
      minInput.value = Number.parseInt(maxInput.value) - 50;
    }
    updateSlider();

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => applyFilters(), 300);
  });

  maxInput.addEventListener('input', () => {
    if (Number.parseInt(maxInput.value) < Number.parseInt(minInput.value) + 50) {
      maxInput.value = Number.parseInt(minInput.value) + 50;
    }
    updateSlider();

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => applyFilters(), 300);
  });

  // Price image clicks
  document.querySelectorAll('.neo-price-filter-image').forEach(img => {
    img.addEventListener('click', () => {
      // Don't allow clicks on disabled images
      if (img.classList.contains('disabled')) {
        return;
      }
      const price = Number.parseInt(img.dataset.price || img.alt);
      if (price && maxInput) {
        maxInput.value = price;
        updateSlider();
        applyFilters();
      }
    });
  });

  // Update disabled state of price filter images
  updatePriceFilterImagesState();

  updateSlider();
}

function clearAllFilters() {
  if (Object.keys(state.activeFilters).length === 0) return;

  state.activeFilters = {};
  state.priceRange.currentMin = state.priceRange.min;
  state.priceRange.currentMax = state.priceRange.max;

  // Remove active class from all filter items
  document.querySelectorAll('.neo-filter-value-item.active').forEach(item => {
    item.classList.remove('active');
  });

  // Update price filter images state (re-enable if needed)
  updatePriceFilterImagesState();

  // Reset price range
  const minInput = document.getElementById('price-range-min');
  const maxInput = document.getElementById('price-range-max');
  if (minInput) minInput.value = state.priceRange.min;
  if (maxInput) maxInput.value = state.priceRange.max;

  initPriceRangeSlider();

  // Update active filter count
  updateActiveFilterCount();

  applyFilters();
}

globalThis.clearAllFilters = clearAllFilters;

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Active Filter Count Update                                              │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function updateActiveFilterCount() {
  // Count all active filter values
  let count = 0;

  // Count filter values from state.activeFilters
  Object.values(state.activeFilters).forEach(values => {
    count += values.length;
  });

  // Count price filter if modified
  if (state.priceRange.currentMin > state.priceRange.min ||
      state.priceRange.currentMax < state.priceRange.max) {
    count += 1;
  }

  // Update all elements with data-id="active-filter-count"
  document.querySelectorAll('[data-id="active-filter-count"]').forEach(element => {
    element.textContent = count > 0 ? count : '';
  });
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Filter Application                                                      │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function applyFilters(invoker = 'applyFilters', invokerPayload) {
  let filteredProductCount = calculateFilteredProductCount();
  generateSkeletonLoaders(Math.min((filteredProductCount || config.productsPerPage), config.productsPerPage), true);
  // generateSkeletonLoaders(config.productsPerPage, true);

  // Build GraphQL filters array
  const graphqlFilters = [];

  Object.entries(state.activeFilters).forEach(([filterId, values]) => {
    values.forEach(value => {
      // Find the original filter to get the input format
      const filter = state.filters.find(f => f.id === filterId);
      if (filter) {
        const filterValue = filter.values.find(v => v.label === value);
        if (filterValue?.input) {
          try {
            graphqlFilters.push(JSON.parse(filterValue.input));
          } catch (e) {
            console.error('Error parsing filter input:', e);
          }
        }
      }
    });
  });

  // Add price filter if modified
  if (state.priceRange.currentMin > state.priceRange.min || state.priceRange.currentMax < state.priceRange.max) {
    graphqlFilters.push({
      price: {
        min: state.priceRange.currentMin,
        max: state.priceRange.currentMax
      }
    });
  }

  // Reset pagination and fetch filtered products
  state.pagination.cursor = null;
  state.pagination.totalLoaded = 0;
  state.allProducts = [];

  // Reset total rendered products count to 0 when filters are applied
  // (since the product grid is cleared and filtered products are rendered)
  state.totalRenderedProducts = 0;
  // Reset buzz in town section inserted flag when filters are applied
  state.buzzInTownSectionInserted = false;

  // Update active filter count
  updateActiveFilterCount();

  fetchAndRenderUI(invoker, graphqlFilters, null, false, invokerPayload);
}

// Helper function to map sort values to GraphQL sort keys
function mapSortValueToGraphQL(sortValue) {
  let sortKey = null;
  let reverse = false;

  switch (sortValue) {
    case 'manual':
      // Price high to low
      sortKey = 'MANUAL';
      break;
    case 'price-ascending':
      // Price low to high
      sortKey = 'PRICE';
      break;
    case 'best-selling':
      sortKey = 'BEST_SELLING';
      break;
    default:
      return null;
  }

  return { sortKey, reverse };
}

function applySort(sortValue, sortTitle) {
  // Map sort values to GraphQL sort keys
  const mapped = mapSortValueToGraphQL(sortValue);
  if (!mapped) {
    console.warn(`Unknown sort value: ${sortValue}`);
    return;
  }

  const { sortKey, reverse } = mapped;

  // Store current sort state (including original sortValue for URL)
  state.currentSort = { sortValue, sortKey, reverse };

  // Get current filters
  const graphqlFilters = buildGraphQLFiltersFromActiveFilters();

  // Clear cache when sorting changes
  state.cache = {};

  // Show loading state
  generateSkeletonLoaders(config.productsPerPage, true);

  // Fetch and re-render products with new sort
  fetchAndRenderUI('applySort', graphqlFilters, sortKey, reverse, { sort_by_value: sortTitle || sortValue });
}

const productCountElement = document.querySelector('[data-id="product-count"]');

function setProductCount(reset = false, count) {
  if (!productCountElement) return;

  if (reset) {
    productCountElement.innerText = productCountElement.dataset.default;
    if (productCountElement.nextElementSibling) {
      productCountElement.nextElementSibling.style.display = null;
    }
    return;
  }

  if (count) {
    productCountElement.innerText = `${count} of ${productCountElement.dataset.default} products`;
    if (productCountElement.nextElementSibling) {
      productCountElement.nextElementSibling.style.display = 'none';
    }
  }
}

/**
 * Returns a stable string representing current filter selection (activeFilters + price).
 * Used to compare with state.filtersContext so we only trust counts when they match.
 */
function getFiltersContext() {
  const active = JSON.stringify(state.activeFilters);
  const price = state.priceRange
    ? `${state.priceRange.currentMin}-${state.priceRange.currentMax}`
    : '';
  return `${active}|${price}`;
}

/**
 * Updates state.filtersContext to the current filter selection. Call whenever state.filters
 * is set from an API response so we know the counts in state.filters are for this selection.
 */
function syncFiltersContext() {
  state.filtersContext = getFiltersContext();
}

/**
* Predicts the filtered product count based purely on facet counts in `state.filters`.
* This is intended to be used **before** a fetch is sent, using the current `state.activeFilters`
* (and price range, if narrowed).
*
* Behaviour:
* - Looks up each selected filter value in `state.filters` and collects its `count`.
* - If a price range is narrowed, also considers the `filter.v.price` count.
* - Returns the minimum of all collected counts (approximate AND-intersection of filters).
* - Returns null when there is no meaningful prediction to make.
*/
function calculateFilteredProductCount() {
  if (!state.filters || state.filters.length === 0) {
    return null;
  }

  const hasPriceFilter =
    state.priceRange &&
    (state.priceRange.currentMin > state.priceRange.min || state.priceRange.currentMax < state.priceRange.max);

  if (!hasActiveFilters() && !hasPriceFilter) {
    return null;
  }

  const selectedCounts = [];

  if (hasActiveFilters()) {
    Object.entries(state.activeFilters).forEach(([filterId, values]) => {
      const filter = state.filters.find((f) => f.id === filterId);
      if (!filter || !Array.isArray(filter.values)) return;

      values.forEach((valueLabel) => {
        const filterValue = filter.values.find((v) => v.label === valueLabel);
        if (filterValue && typeof filterValue.count === 'number') {
          selectedCounts.push(filterValue.count);
        }
      });
    });
  }

  if (hasPriceFilter) {
    const priceFilter = state.filters.find((f) => f.id === 'filter.v.price');
    if (priceFilter && Array.isArray(priceFilter.values) && priceFilter.values.length > 0) {
      const priceValue = priceFilter.values[0];
      if (priceValue && typeof priceValue.count === 'number') {
        selectedCounts.push(priceValue.count);
      }
    }
  }

  if (selectedCounts.length === 0) {
    return null;
  }

  return Math.min(...selectedCounts);
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Generates Skeletons Loaders                                             │
  │ Replace - Boolean - [True] Replaces product/skeleton slots only;        │
  │           produces exactly `count` skeletons.                           │
  │ Replace - Boolean - [False] Appends `count` skeletons after grid.       │
  └─────────────────────────────────────────────────────────────────────────┘
*/
function generateSkeletonLoaders(count, replace = false) {
  const productGrid = document.getElementById('product-grid');
  if (!productGrid) return;

  const skeletonHTML = `<li class="grid__item grid__item--skeleton">
    <skeleton style="
      --skeleton-width: 100%;
      --skeleton-height: 100%;
      --skeleton-min-height: 537px;
      --skeleton-radius: 16px;
      --skeleton-bg: rgba(170, 170, 170, 0.2);
      --skeleton-shine-color: rgba(255, 255, 255, 0.3);
      --skeleton-animation-duration: 1.2s;">
    </skeleton>
  </li>`;

  if (replace) {
    // Only replace product slots and existing skeletons (exclude in-grid inserts like size filter, buzz-in-town)
    const replaceableItems = Array.from(productGrid.querySelectorAll('li.grid__item')).filter((item) => item.classList.contains('grid__item--skeleton') || (item.querySelector('.new_card_product') && !item.querySelector('.buzz-in-town-section')));

    replaceableItems.forEach((item) => {
      if (item.parentNode) {
        item.outerHTML = skeletonHTML;
      }
    });

    // Ensure exactly `count` skeletons: remove excess or append more
    const skeletonElements = productGrid.querySelectorAll('.grid__item--skeleton');
    const currentCount = skeletonElements.length;
    if (currentCount > count) {
      Array.from(skeletonElements).slice(count).forEach((s) => s.remove());
    } else if (currentCount < count) {
      productGrid.insertAdjacentHTML('beforeend', skeletonHTML.repeat(count - currentCount));
    }
  } else {
    // Append new skeletons (bulk operation - single reflow)
    productGrid.insertAdjacentHTML('beforeend', skeletonHTML.repeat(count));
  }
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Product Card Rendering                                                  │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function formatPrice(amount) {
  const price = Number.parseFloat(amount);
  return `₹${Math.floor(price)}`;
}

function getProductImage(product, index = 0) {
  const images = product.images?.nodes || [];
  let imageData = null;

  // If there are more than 4 images, use the 5th image (index 4)
  if (images.length > 4) {
    imageData = images[4];
  } else if (images.length > 0) {
    imageData = images[0];
  }

  if (!imageData) return null;

  const src = imageData.src || imageData.url;
  const widths = [220, 360, 420];
  const srcset = widths.map(w => {
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}width=${w} ${w}w`;
  }).join(', ');

  return {
    src: `${src}${src.includes('?') ? '&' : '?'}width=420`,
    srcset,
    alt: imageData.altText || product.title,
    width: imageData.width || 420,
    height: imageData.height || 513
  };
}

function getColorVariantCount(product) {
  // Prefer resolved references (matches Liquid `forloop.length` on metafield value)
  const nodes = product?.color_variant?.references?.nodes;
  if (Array.isArray(nodes) && nodes.length > 0) {
    return nodes.length;
  }

  const raw = product?.color_variant?.value;
  if (!raw) return 0;

  const asString = typeof raw === 'string' ? raw.trim() : String(raw ?? '').trim();
  if (!asString) return 0;

  const toIdSet = (values) => {
    const set = new Set();
    for (const v of values) {
      const s = String(v ?? '').trim();
      if (!s) continue;
      // Normalize both GIDs and numeric ids into numeric string key when possible.
      const match = s.match(/(\d+)(?!.*\d)/); // last number in string
      set.add(match ? match[1] : s);
    }
    return set;
  };

  // Primary: JSON array/string
  try {
    const parsed = JSON.parse(asString);
    if (Array.isArray(parsed)) return toIdSet(parsed).size;
    if (typeof parsed === 'string') return toIdSet([parsed]).size;
    // Any other JSON type (number/object) counts as a single entry
    return 1;
  } catch {
    // Fallback: extract ids from non-JSON string formats
    const extracted = asString.match(/gid:\/\/shopify\/Product\/\d+|\b\d+\b/g) || [];
    if (extracted.length > 0) return toIdSet(extracted).size;

    // Last-resort: comma-separated string
    if (asString.includes(',')) return toIdSet(asString.split(',')).size;
    return 1;
  }
}

function formatMetafieldValue(metafield) {
  if (!metafield?.value) return '';
  try {
    const parsed = JSON.parse(metafield.value);
    return Array.isArray(parsed) ? parsed.join('') : String(metafield.value);
  } catch {
    return String(metafield.value);
  }
}

function generateProductCardHTML(product, index) {
  const productId = product.id.replace('gid://shopify/Product/', '');
  const colorVariantsIds = (() => {
    try {
      return JSON.parse(product.color_variant?.value || '[]').map(id => id.split('/').pop()).join(',');
    } catch { return ''; }
  })();

  const productUrl = `/products/${product.handle}`;
  const productTitle = product.title;
  const styleNumber = product.style_number?.value || '';

  // Get image
  const primaryImage = getProductImage(product);
  const imageSrc = primaryImage?.src || '';
  const imageAlt = primaryImage?.alt || productTitle;

  // Price
  const minPrice = Number.parseFloat(product.priceRange?.minVariantPrice?.amount || 0);
  const formattedPrice = formatPrice(minPrice);

  // Tags
  const tags = product.tags || [];
  const isBestseller = tags.includes('Bestseller');
  const isNewStyle = tags.some(tag => ['New Style', 'new style', 'NEW STYLE'].includes(tag));
  const hasOneDayDelivery = tags.includes('1 day delivery');
  const hasMultipackCombo = tags.includes('multipack_combo');
  const isJkyStudio = tags.includes('JKY Studio');

  // Color variant badge
  const colorVariantCount = getColorVariantCount(product);
  const colorVariantBadge = colorVariantCount > 1 && !isJkyStudio ? `
    <div class="card-five-svg" onclick="ATBModalOpen(event, '${productId}', '${colorVariantsIds}', '${product.select_size_guide?.value || ''}', 'category-page')">
      <div class="card-five-svg-inner">
        <p class="card-five-number">${colorVariantCount}</p>
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="37" viewBox="0 0 48 37" fill="none">
          <rect x="28.98" y="5.774" width="18.281" height="25.313" rx="3.516" fill="#C46B4F" stroke="#fff" stroke-width="1.406"/>
          <rect x=".855" y="5.774" width="16.875" height="25.313" rx="3.516" fill="#557CA6" stroke="#fff" stroke-width="1.406"/>
          <rect x="10.699" y="1.556" width="26.719" height="33.75" rx="6.328" fill="#464F51" stroke="#fff" stroke-width="1.406"/>
        </svg>
      </div>
      <svg class="loading-spinner" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><style>.spinner_aj0A{transform-origin:center;animation:spinner_KYSC .75s infinite linear}@keyframes spinner_KYSC{100%{transform:rotate(360deg)}}</style><path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z" class="spinner_aj0A"/></svg>
    </div>
  ` : '';

  // Bestseller/New tag
  const bestsellerTag = isBestseller ? `
    <div class="bestsellerTag">
      <svg width="32" height="71" viewBox="0 0 32 71" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M29.7966 0.781671C30.3731 0.748469 31.073 2.68026 31.5712 5.36966H28.5586C28.5616 4.79616 28.612 3.93667 28.8604 2.92927C29.0816 2.03205 29.3827 0.805819 29.7966 0.781671Z" fill="#232323"></path>
      <path d="M2.34883 0.781671C1.77235 0.748469 1.07243 2.68101 0.574219 5.37041H3.58684C3.58383 4.79691 3.53341 3.93742 3.28505 2.93003C3.06379 2.03205 2.76351 0.805819 2.34883 0.781671Z" fill="#232323"></path>
      <path d="M29.5742 0.394418C29.526 0.391573 29.4764 0.390625 29.4252 0.390625C29.3763 0.390625 29.3281 0.391573 29.2822 0.393469H2.86625C2.8203 0.391573 2.77286 0.390625 2.7232 0.390625C2.67206 0.390625 2.6224 0.392521 2.57422 0.394418C2.77434 0.892196 2.91369 1.31223 3.0019 1.59382C3.64082 3.63425 3.77868 5.37885 3.77646 6.21132C3.77572 6.56877 3.77498 6.92622 3.7735 7.28272H3.77646V70.3906C5.16771 68.6109 16.0738 56.3732 16.0738 56.3732C16.0738 56.3732 26.98 68.6109 28.3712 70.3906V7.28368H28.3742C28.3734 6.92623 28.3727 6.56972 28.3712 6.21227C28.369 5.37979 28.5069 3.6352 29.1458 1.59477C29.234 1.31317 29.3733 0.893144 29.5735 0.395366L29.5742 0.394418Z" fill="#3D3B3B"></path>
      <path d="M16.7728 9.86062L17.2428 9.86062L17.2428 10.9906C17.2428 11.224 17.2828 11.434 17.3628 11.6206C17.4428 11.8073 17.5594 11.954 17.7128 12.0606C17.8661 12.1673 18.0528 12.2206 18.2728 12.2206C18.6061 12.2206 18.8628 12.104 19.0428 11.8706C19.2294 11.6373 19.3228 11.344 19.3228 10.9906L19.3228 10.0406L13.8228 10.0406L13.8228 11.0906C13.8228 11.3906 13.8761 11.654 13.9828 11.8806C14.0894 12.114 14.2428 12.294 14.4428 12.4206C14.6428 12.554 14.8861 12.6206 15.1728 12.6206C15.3861 12.6206 15.5761 12.584 15.7428 12.5106C15.9094 12.4373 16.0494 12.3306 16.1628 12.1906C16.2761 12.0573 16.3594 11.8973 16.4128 11.7106C16.4728 11.524 16.5028 11.3173 16.5028 11.0906L16.5028 9.86062L16.9728 9.86062L16.9728 11.0906C16.9728 11.4306 16.9361 11.744 16.8628 12.0306C16.7894 12.3173 16.6761 12.5673 16.5228 12.7806C16.3761 13.0006 16.1861 13.1706 15.9528 13.2906C15.7194 13.4106 15.4428 13.4706 15.1228 13.4706C14.6628 13.4706 14.2794 13.364 13.9728 13.1506C13.6728 12.944 13.4461 12.6606 13.2928 12.3006C13.1461 11.9473 13.0728 11.544 13.0728 11.0906L13.0728 9.19062L20.0728 9.19063L20.0728 10.9906C20.0728 11.4106 20.0094 11.774 19.8828 12.0806C19.7561 12.394 19.5628 12.6373 19.3028 12.8106C19.0494 12.984 18.7228 13.0706 18.3228 13.0706C17.9961 13.0706 17.7161 12.984 17.4828 12.8106C17.2494 12.6373 17.0728 12.394 16.9528 12.0806C16.8328 11.774 16.7728 11.4106 16.7728 10.9906L16.7728 9.86062ZM12.9728 16.4968C12.9728 16.0701 13.0728 15.6901 13.2728 15.3568C13.4794 15.0301 13.7628 14.7735 14.1228 14.5868C14.4828 14.4068 14.8994 14.3168 15.3728 14.3168C15.8528 14.3168 16.2728 14.4101 16.6328 14.5968C16.9928 14.7901 17.2728 15.0568 17.4728 15.3968C17.6728 15.7435 17.7728 16.1435 17.7728 16.5968C17.7728 17.0501 17.6761 17.4368 17.4828 17.7568C17.2961 18.0835 17.0261 18.3335 16.6728 18.5068C16.3194 18.6801 15.8994 18.7668 15.4128 18.7668C15.3661 18.7668 15.3161 18.7635 15.2628 18.7568C15.2161 18.7568 15.1861 18.7568 15.1728 18.7568L15.1728 14.8268L15.8528 14.8268L15.8528 18.1268L15.3928 17.8068C15.4261 17.8268 15.4761 17.8468 15.5428 17.8668C15.6161 17.8868 15.6761 17.8968 15.7228 17.8968C15.9961 17.8968 16.2328 17.8401 16.4328 17.7268C16.6328 17.6201 16.7894 17.4701 16.9028 17.2768C17.0161 17.0835 17.0728 16.8568 17.0728 16.5968C17.0728 16.2901 17.0061 16.0268 16.8728 15.8068C16.7461 15.5935 16.5594 15.4268 16.3128 15.3068C16.0728 15.1935 15.7761 15.1335 15.4228 15.1268C15.0628 15.1268 14.7561 15.1835 14.5028 15.2968C14.2494 15.4101 14.0561 15.5768 13.9228 15.7968C13.7894 16.0168 13.7228 16.2835 13.7228 16.5968C13.7228 16.9101 13.7894 17.1868 13.9228 17.4268C14.0628 17.6668 14.2728 17.8701 14.5528 18.0368L14.1428 18.6868C13.7561 18.4468 13.4628 18.1435 13.2628 17.7768C13.0694 17.4168 12.9728 16.9901 12.9728 16.4968ZM14.4828 20.0533C14.3428 20.1466 14.2128 20.2533 14.0928 20.3733C13.9728 20.4933 13.8761 20.6299 13.8028 20.7833C13.7361 20.9433 13.7028 21.1166 13.7028 21.3033C13.7028 21.5299 13.7528 21.7066 13.8528 21.8333C13.9594 21.9666 14.1028 22.0333 14.2828 22.0333C14.4428 22.0333 14.5761 21.9799 14.6828 21.8733C14.7961 21.7733 14.8894 21.6433 14.9628 21.4833C15.0428 21.3233 15.1161 21.1533 15.1828 20.9733C15.2628 20.7733 15.3561 20.5699 15.4628 20.3633C15.5761 20.1633 15.7194 19.9933 15.8928 19.8533C16.0728 19.7199 16.2961 19.6533 16.5628 19.6533C16.8361 19.6533 17.0628 19.7233 17.2428 19.8633C17.4228 20.0099 17.5561 20.1966 17.6428 20.4233C17.7294 20.6566 17.7728 20.8966 17.7728 21.1433C17.7728 21.3899 17.7328 21.6199 17.6528 21.8333C17.5728 22.0533 17.4661 22.2466 17.3328 22.4133C17.1994 22.5799 17.0461 22.7099 16.8728 22.8033L16.4628 22.1633C16.6361 22.0366 16.7794 21.8766 16.8928 21.6833C17.0061 21.4966 17.0628 21.2833 17.0628 21.0433C17.0628 20.8766 17.0261 20.7333 16.9528 20.6133C16.8794 20.4933 16.7661 20.4333 16.6128 20.4333C16.4928 20.4333 16.3861 20.4799 16.2928 20.5733C16.1994 20.6666 16.1161 20.7866 16.0428 20.9333C15.9694 21.0799 15.8994 21.2333 15.8328 21.3933C15.7261 21.6599 15.6094 21.9033 15.4828 22.1233C15.3628 22.3433 15.2161 22.5166 15.0428 22.6433C14.8694 22.7766 14.6461 22.8433 14.3728 22.8433C13.9728 22.8433 13.6394 22.6966 13.3728 22.4033C13.1061 22.1166 12.9728 21.7366 12.9728 21.2633C12.9728 20.9566 13.0261 20.6799 13.1328 20.4333C13.2461 20.1866 13.3894 19.9766 13.5628 19.8033C13.7361 19.6366 13.9128 19.5099 14.0928 19.4233L14.4828 20.0533ZM17.6728 23.1965L17.6728 25.4965L16.9228 25.4965L16.9228 23.1965L17.6728 23.1965ZM19.2728 23.9465L19.2728 24.7465L13.0728 24.7465L13.0728 23.9465L19.2728 23.9465ZM14.4828 26.4888C14.3428 26.5822 14.2128 26.6888 14.0928 26.8088C13.9728 26.9288 13.8761 27.0655 13.8028 27.2188C13.7361 27.3788 13.7028 27.5522 13.7028 27.7388C13.7028 27.9655 13.7528 28.1422 13.8528 28.2688C13.9594 28.4022 14.1028 28.4688 14.2828 28.4688C14.4428 28.4688 14.5761 28.4155 14.6828 28.3088C14.7961 28.2088 14.8894 28.0788 14.9628 27.9188C15.0428 27.7588 15.1161 27.5888 15.1828 27.4088C15.2628 27.2088 15.3561 27.0055 15.4628 26.7988C15.5761 26.5988 15.7194 26.4288 15.8928 26.2888C16.0728 26.1555 16.2961 26.0888 16.5628 26.0888C16.8361 26.0888 17.0628 26.1588 17.2428 26.2988C17.4228 26.4455 17.5561 26.6322 17.6428 26.8588C17.7294 27.0922 17.7728 27.3322 17.7728 27.5788C17.7728 27.8255 17.7328 28.0555 17.6528 28.2688C17.5728 28.4888 17.4661 28.6822 17.3328 28.8488C17.1994 29.0155 17.0461 29.1455 16.8728 29.2388L16.4628 28.5988C16.6361 28.4722 16.7794 28.3122 16.8928 28.1188C17.0061 27.9322 17.0628 27.7188 17.0628 27.4788C17.0628 27.3122 17.0261 27.1688 16.9528 27.0488C16.8794 26.9288 16.7661 26.8688 16.6128 26.8688C16.4928 26.8688 16.3861 26.9155 16.2928 27.0088C16.1994 27.1022 16.1161 27.2222 16.0428 27.3688C15.9694 27.5155 15.8994 27.6688 15.8328 27.8288C15.7261 28.0955 15.6094 28.3388 15.4828 28.5588C15.3628 28.7788 15.2161 28.9522 15.0428 29.0788C14.8694 29.2122 14.6461 29.2788 14.3728 29.2788C13.9728 29.2788 13.6394 29.1322 13.3728 28.8388C13.1061 28.5522 12.9728 28.1722 12.9728 27.6988C12.9728 27.3922 13.0261 27.1155 13.1328 26.8688C13.2461 26.6222 13.3894 26.4122 13.5628 26.2388C13.7361 26.0722 13.9128 25.9455 14.0928 25.8588L14.4828 26.4888ZM12.9728 32.112C12.9728 31.6854 13.0728 31.3054 13.2728 30.972C13.4794 30.6454 13.7628 30.3887 14.1228 30.202C14.4828 30.022 14.8994 29.932 15.3728 29.932C15.8528 29.932 16.2728 30.0254 16.6328 30.212C16.9928 30.4054 17.2728 30.672 17.4728 31.012C17.6728 31.3587 17.7728 31.7587 17.7728 32.212C17.7728 32.6654 17.6761 33.052 17.4828 33.372C17.2961 33.6987 17.0261 33.9487 16.6728 34.122C16.3194 34.2954 15.8994 34.382 15.4128 34.382C15.3661 34.382 15.3161 34.3787 15.2628 34.372C15.2161 34.372 15.1861 34.372 15.1728 34.372L15.1728 30.442L15.8528 30.442L15.8528 33.742L15.3928 33.422C15.4261 33.442 15.4761 33.462 15.5428 33.482C15.6161 33.502 15.6761 33.512 15.7228 33.512C15.9961 33.512 16.2328 33.4554 16.4328 33.342C16.6328 33.2354 16.7894 33.0854 16.9028 32.892C17.0161 32.6987 17.0728 32.472 17.0728 32.212C17.0728 31.9054 17.0061 31.642 16.8728 31.422C16.7461 31.2087 16.5594 31.042 16.3128 30.922C16.0728 30.8087 15.7761 30.7487 15.4228 30.742C15.0628 30.742 14.7561 30.7987 14.5028 30.912C14.2494 31.0254 14.0561 31.192 13.9228 31.412C13.7894 31.632 13.7228 31.8987 13.7228 32.212C13.7228 32.5254 13.7894 32.802 13.9228 33.042C14.0628 33.282 14.2728 33.4854 14.5528 33.652L14.1428 34.302C13.7561 34.062 13.4628 33.7587 13.2628 33.392C13.0694 33.032 12.9728 32.6054 12.9728 32.112ZM20.8728 35.4785L20.8728 36.2785L13.0728 36.2785L13.0728 35.4785L20.8728 35.4785ZM20.8728 37.7832L20.8728 38.5832L13.0728 38.5832L13.0728 37.7832L20.8728 37.7832ZM12.9728 41.8679C12.9728 41.4412 13.0728 41.0612 13.2728 40.7279C13.4794 40.4012 13.7628 40.1446 14.1228 39.9579C14.4828 39.7779 14.8994 39.6879 15.3728 39.6879C15.8528 39.6879 16.2728 39.7812 16.6328 39.9679C16.9928 40.1612 17.2728 40.4279 17.4728 40.7679C17.6728 41.1146 17.7728 41.5146 17.7728 41.9679C17.7728 42.4212 17.6761 42.8079 17.4828 43.1279C17.2961 43.4546 17.0261 43.7046 16.6728 43.8779C16.3194 44.0512 15.8994 44.1379 15.4128 44.1379C15.3661 44.1379 15.3161 44.1346 15.2628 44.1279C15.2161 44.1279 15.1861 44.1279 15.1728 44.1279L15.1728 40.1979L15.8528 40.1979L15.8528 43.4979L15.3928 43.1779C15.4261 43.1979 15.4761 43.2179 15.5428 43.2379C15.6161 43.2579 15.6761 43.2679 15.7228 43.2679C15.9961 43.2679 16.2328 43.2112 16.4328 43.0979C16.6328 42.9912 16.7894 42.8412 16.9028 42.6479C17.0161 42.4546 17.0728 42.2279 17.0728 41.9679C17.0728 41.6612 17.0061 41.3979 16.8728 41.1779C16.7461 40.9646 16.5594 40.7979 16.3128 40.6779C16.0728 40.5646 15.7761 40.5046 15.4228 40.4979C15.0628 40.4979 14.7561 40.5546 14.5028 40.6679C14.2494 40.7812 14.0561 40.9479 13.9228 41.1679C13.7894 41.3879 13.7228 41.6546 13.7228 41.9679C13.7228 42.2812 13.7894 42.5579 13.9228 42.7979C14.0628 43.0379 14.2728 43.2412 14.5528 43.4079L14.1428 44.0579C13.7561 43.8179 13.4628 43.5146 13.2628 43.1479C13.0694 42.7879 12.9728 42.3612 12.9728 41.8679ZM17.6728 46.0344L13.0728 46.0344L13.0728 45.2344L17.6728 45.2344L17.6728 46.0344ZM16.7928 47.4244C16.8728 47.3177 16.9294 47.221 16.9628 47.1344C17.0028 47.0477 17.0228 46.9377 17.0228 46.8044C17.0228 46.6044 16.9728 46.4477 16.8728 46.3344C16.7728 46.221 16.6361 46.141 16.4628 46.0944C16.2894 46.0544 16.0928 46.0344 15.8728 46.0344L15.8728 45.6744C16.2394 45.6744 16.5628 45.7377 16.8428 45.8644C17.1294 45.9977 17.3561 46.1677 17.5228 46.3744C17.6894 46.581 17.7728 46.791 17.7728 47.0044C17.7728 47.171 17.7494 47.3244 17.7028 47.4644C17.6628 47.611 17.5794 47.7444 17.4528 47.8644L16.7928 47.4244Z" fill="white"></path>
    </svg>
    </div>
  ` : (isNewStyle ? `
    <div class="bestsellerTag new-plp-tag">
        <svg xmlns="http://www.w3.org/2000/svg" width="27" height="70" viewBox="0 0 27 70" fill="none">
          <path d="M26.9993 0.00665138C26.9996 0.00606097 26.9992 0.00533211 26.9986 0.00527649C26.9508 0.00132239 26.9017 0 26.851 0C26.8021 0 26.7539 0.00134889 26.708 0.00404667H0.292036C0.246081 0.00134889 0.198644 0 0.148983 0C0.0724897 0 0.0197703 0.0753204 0.04019 0.149038C0.218937 0.794334 0.345503 1.3385 0.427677 1.71174C1.0666 4.61455 1.20446 7.0965 1.20224 8.28083C1.2015 8.78886 1.20076 9.2969 1.19928 9.80359C1.19928 9.80441 1.19994 9.80507 1.20076 9.80507C1.20158 9.80507 1.20224 9.80574 1.20224 9.80655V57.2522C1.20224 64.2926 13.95 70 13.95 70C13.95 70 25.797 64.6959 25.797 58.153V9.8079C25.797 9.80708 25.7977 9.80642 25.7985 9.80642C25.7993 9.80642 25.8 9.80576 25.8 9.80494C25.7992 9.2969 25.7985 8.79021 25.797 8.28218C25.7948 7.09785 25.9327 4.6159 26.5716 1.71309C26.6598 1.31249 26.7991 0.714969 26.9992 0.00684714C26.9992 0.00677923 26.9993 0.00671322 26.9993 0.00665138Z" fill="url(#paint0_linear_8849909317931)"></path>
          <path d="M18.2734 14.1L18.2734 14.95L10.9234 14.95L16.4834 9.65L11.2734 9.65L11.2734 8.8L18.6234 8.8L13.0634 14.1L18.2734 14.1ZM11.1734 18.2839C11.1734 17.8572 11.2734 17.4772 11.4734 17.1439C11.6801 16.8172 11.9634 16.5606 12.3234 16.3739C12.6834 16.1939 13.1001 16.1039 13.5734 16.1039C14.0534 16.1039 14.4734 16.1972 14.8334 16.3839C15.1934 16.5772 15.4734 16.8439 15.6734 17.1839C15.8734 17.5306 15.9734 17.9306 15.9734 18.3839C15.9734 18.8372 15.8768 19.2239 15.6834 19.5439C15.4968 19.8706 15.2268 20.1206 14.8734 20.2939C14.5201 20.4672 14.1001 20.5539 13.6134 20.5539C13.5668 20.5539 13.5168 20.5506 13.4634 20.5439C13.4168 20.5439 13.3868 20.5439 13.3734 20.5439L13.3734 16.6139L14.0534 16.6139L14.0534 19.9139L13.5934 19.5939C13.6268 19.6139 13.6768 19.6339 13.7434 19.6539C13.8168 19.6739 13.8768 19.6839 13.9234 19.6839C14.1968 19.6839 14.4334 19.6272 14.6334 19.5139C14.8334 19.4072 14.9901 19.2572 15.1034 19.0639C15.2168 18.8706 15.2734 18.6439 15.2734 18.3839C15.2734 18.0772 15.2068 17.8139 15.0734 17.5939C14.9468 17.3806 14.7601 17.2139 14.5134 17.0939C14.2734 16.9806 13.9768 16.9206 13.6234 16.9139C13.2634 16.9139 12.9568 16.9706 12.7034 17.0839C12.4501 17.1972 12.2568 17.3639 12.1234 17.5839C11.9901 17.8039 11.9234 18.0706 11.9234 18.3839C11.9234 18.6972 11.9901 18.9739 12.1234 19.2139C12.2634 19.4539 12.4734 19.6572 12.7534 19.8239L12.3434 20.4739C11.9568 20.2339 11.6634 19.9306 11.4634 19.5639C11.2701 19.2039 11.1734 18.7772 11.1734 18.2839ZM15.8734 20.8039L15.8734 21.7039L12.8834 22.8539L16.2234 24.1039L12.8634 25.4439L15.8734 26.6039L15.8734 27.5039L10.9234 25.5039L14.2734 24.1139L10.9234 22.8039L15.8734 20.8039ZM13.3734 32.0188L14.1734 32.3188L14.1734 35.4188L13.3734 35.7188L13.3734 32.0188ZM16.6934 33.8488L13.9134 32.6688L13.7334 32.5888L11.2734 31.5488L11.2734 30.5988L18.6234 33.8488L11.2734 37.0988L11.2734 36.1488L13.6734 35.1288L13.8734 35.0488L16.6934 33.8488ZM15.8734 38.7004L11.2734 38.7004L11.2734 37.9004L15.8734 37.9004L15.8734 38.7004ZM14.9934 40.0904C15.0734 39.9837 15.1301 39.8871 15.1634 39.8004C15.2034 39.7137 15.2234 39.6037 15.2234 39.4704C15.2234 39.2704 15.1734 39.1137 15.0734 39.0004C14.9734 38.8871 14.8368 38.8071 14.6634 38.7604C14.4901 38.7204 14.2934 38.7004 14.0734 38.7004L14.0734 38.3404C14.4401 38.3404 14.7634 38.4037 15.0434 38.5304C15.3301 38.6637 15.5568 38.8337 15.7234 39.0404C15.8901 39.2471 15.9734 39.4571 15.9734 39.6704C15.9734 39.8371 15.9501 39.9904 15.9034 40.1304C15.8634 40.2771 15.7801 40.4104 15.6534 40.5304L14.9934 40.0904ZM15.8734 42.1281L11.2734 42.1281L11.2734 41.3281L15.8734 41.3281L15.8734 42.1281ZM14.9934 43.5181C15.0734 43.4115 15.1301 43.3148 15.1634 43.2281C15.2034 43.1415 15.2234 43.0315 15.2234 42.8981C15.2234 42.6981 15.1734 42.5415 15.0734 42.4281C14.9734 42.3148 14.8368 42.2348 14.6634 42.1881C14.4901 42.1481 14.2934 42.1281 14.0734 42.1281L14.0734 41.7681C14.4401 41.7681 14.7634 41.8315 15.0434 41.9581C15.3301 42.0915 15.5568 42.2615 15.7234 42.4681C15.8901 42.6748 15.9734 42.8848 15.9734 43.0981C15.9734 43.2648 15.9501 43.4181 15.9034 43.5581C15.8634 43.7048 15.7801 43.8381 15.6534 43.9581L14.9934 43.5181ZM17.7734 44.7559C17.9268 44.7559 18.0568 44.8092 18.1634 44.9159C18.2701 45.0292 18.3234 45.1592 18.3234 45.3059C18.3234 45.4592 18.2701 45.5892 18.1634 45.6959C18.0568 45.8025 17.9268 45.8559 17.7734 45.8559C17.6268 45.8559 17.4968 45.8025 17.3834 45.6959C17.2768 45.5892 17.2234 45.4592 17.2234 45.3059C17.2234 45.1592 17.2768 45.0292 17.3834 44.9159C17.4968 44.8092 17.6268 44.7559 17.7734 44.7559ZM15.8734 44.9059L15.8734 45.7059L11.2734 45.7059L11.2734 44.9059L15.8734 44.9059ZM15.8734 46.6035L15.8734 47.5035L12.6234 48.8035L15.8734 50.1035L15.8734 51.0035L10.9234 48.8035L15.8734 46.6035ZM12.6934 52.1178C12.5201 52.1178 12.3701 52.1578 12.2434 52.2378C12.1168 52.3245 12.0201 52.4411 11.9534 52.5878C11.8934 52.7411 11.8634 52.9145 11.8634 53.1078C11.8634 53.3545 11.9134 53.5745 12.0134 53.7678C12.1134 53.9678 12.2534 54.1245 12.4334 54.2378C12.6201 54.3578 12.8334 54.4178 13.0734 54.4178L12.4734 54.5778C12.1801 54.5778 11.9368 54.4878 11.7434 54.3078C11.5501 54.1345 11.4068 53.9178 11.3134 53.6578C11.2201 53.4045 11.1734 53.1511 11.1734 52.8978C11.1734 52.6178 11.2301 52.3545 11.3434 52.1078C11.4634 51.8678 11.6334 51.6745 11.8534 51.5278C12.0734 51.3811 12.3401 51.3078 12.6534 51.3078C13.1001 51.3078 13.4568 51.4645 13.7234 51.7778C13.9968 52.0978 14.1334 52.5411 14.1334 53.1078C14.1334 53.4411 14.0934 53.7178 14.0134 53.9378C13.9401 54.1645 13.8534 54.3445 13.7534 54.4778C13.6601 54.6111 13.5834 54.7011 13.5234 54.7478L13.0534 54.7478C13.2134 54.5145 13.3268 54.2745 13.3934 54.0278C13.4668 53.7811 13.5034 53.5178 13.5034 53.2378C13.5034 52.9778 13.4701 52.7645 13.4034 52.5978C13.3368 52.4378 13.2434 52.3178 13.1234 52.2378C13.0034 52.1578 12.8601 52.1178 12.6934 52.1178ZM14.8134 51.9978L15.4034 51.6078C15.5301 51.7811 15.6568 52.0178 15.7834 52.3178C15.9101 52.6245 15.9734 52.9911 15.9734 53.4178C15.9734 53.7778 15.9168 54.0911 15.8034 54.3578C15.6968 54.6311 15.5401 54.8411 15.3334 54.9878C15.1268 55.1411 14.8734 55.2178 14.5734 55.2178L11.2734 55.2178L11.2734 54.4178L14.4634 54.4178C14.6234 54.4178 14.7534 54.3911 14.8534 54.3378C14.9601 54.2845 15.0434 54.2078 15.1034 54.1078C15.1634 54.0145 15.2068 53.9045 15.2334 53.7778C15.2601 53.6511 15.2734 53.5178 15.2734 53.3778C15.2734 53.1511 15.2468 52.9445 15.1934 52.7578C15.1401 52.5778 15.0768 52.4245 15.0034 52.2978C14.9301 52.1711 14.8668 52.0711 14.8134 51.9978ZM19.0734 56.5723L19.0734 57.3723L11.2734 57.3723L11.2734 56.5723L19.0734 56.5723Z" fill="white"></path>
          <defs>
            <linearGradient id="paint0_linear_8849909317931" x1="13.4885" y1="4.11276" x2="11.785" y2="181.902" gradientUnits="userSpaceOnUse">
              <stop offset="0.14" stop-color="#3D3B3B"></stop>
              <stop offset="0.9" stop-color="#221F20"></stop>
            </linearGradient>
          </defs>
        </svg>
    </div>
  ` : '');

  // UOM quantity / multipack
  const uomQuantity = Number.parseInt(product.uom_quantity?.value) || 1;
  const firstVariant = product.variants?.edges?.[0]?.node;
  const compareAtPrice = Number.parseFloat(firstVariant?.compareAtPrice?.amount || 0);
  const variantPrice = Number.parseFloat(firstVariant?.price?.amount || 0);

  let styleNoDateContent = '';
  if (styleNumber) {
    styleNoDateContent += `<p class="productstyleNumber">${styleNumber}</p>`;
  }
  if (hasOneDayDelivery) {
    styleNoDateContent += '<p class="delivarydate">1 day delivery</p>';
  }
  if (compareAtPrice > variantPrice && hasMultipackCombo) {
    const savings = Math.floor(compareAtPrice - variantPrice);
    styleNoDateContent += `<span class="saving-zone-multi-packs multipack_combo"><span class="savingAmt">Save ₹${savings}</span><span> with Pack of</span> ${uomQuantity}</span>`;
  } else if (uomQuantity > 1 && !hasMultipackCombo) {
    styleNoDateContent += `<span class="saving-zone-multi-packs"><span>Pack of</span> ${uomQuantity}</span>`;
  }

  // Format data attributes
  const jockeyCollection = formatMetafieldValue(product.jockey_collection);
  const fabric = formatMetafieldValue(product.fabric);
  const braPaddingFilter = formatMetafieldValue(product.bra_padding_filter);
  const packMultipacksFilter = formatMetafieldValue(product.pack_multipacks_Filter);
  const closureFilter = formatMetafieldValue(product.closure_filter);
  const waistRise = formatMetafieldValue(product.waist_rise);
  const braWiringFilter = formatMetafieldValue(product.bra_wiring_filter);
  const backStyleFilter = formatMetafieldValue(product.back_style_filter);
  const strapsFilter = formatMetafieldValue(product.straps_filter);
  const braFrontCoverageFilter = formatMetafieldValue(product.bra_front_coverage_filter);
  const topRearCoverageFilter = formatMetafieldValue(product.top_rear_coverage_filter);
  const rearCoverageFilter = formatMetafieldValue(product.rear_coverage_Filter);
  const filterColour = formatMetafieldValue(product.filter_colour);
  const applicableFilters = formatMetafieldValue(product.bestseller_all_applicable_filter_values);
  const options = product.options?.map(opt => opt.values.join(',')).join(',') || '';
  const maxPriceFormatted = Math.floor(Number.parseFloat(product.priceRange?.maxVariantPrice?.amount || 0));

  return `
    <li
      data-productID="${productId}"
      data-jockey-collection="${jockeyCollection}"
      data-fabric="${fabric}"
      data-bra-padding-filter="${braPaddingFilter}"
      data-pack-multipacks-filter="${packMultipacksFilter}"
      data-closure-filter="${closureFilter}"
      data-waist-rise="${waistRise}"
      data-bra-wiring-filter="${braWiringFilter}"
      data-back-style-filter="${backStyleFilter}"
      data-straps-filter="${strapsFilter}"
      data-bra-front-coverage-filter="${braFrontCoverageFilter}"
      data-top-rear-coverage-filter="${topRearCoverageFilter}"
      data-rear-coverage-filter="${rearCoverageFilter}"
      data-filter-colour="${filterColour}"
      data-product-all-filters="#"
      data-product-type="${product.productType || ''}"
      data-styleNumber="${styleNumber}"
      data-applicableFilters="${applicableFilters}"
      data-options="${options}"
      data-maxPrice="${maxPriceFormatted}"
      class="colorVariant grid__item loop_index_${index} page_index_1"
    >
      <div class="new_card_product d${productId}" data-id="dataInJson" data-product-id="d${productId}">
        ${bestsellerTag}
        <div class="new_card_product_content">
          <div class="card_wishlist">

            <span
              class="smartwishlist wishlistcardButton"
              data-product="${productId}"
              data-variant="${firstVariant?.id?.replace('gid://shopify/ProductVariant/', '') || ''}"
              onclick="wishlistEvent(this)"
            ></span>
          </div>

          <div class="new_card_product_media">
            <div class="swiper mySwiper collection-cardSwiper swiperWidth">
              <div class="swiper-wrapper">
                <div class="sld-img">
                  <a href="${productUrl}">
                    <img
                      src="${imageSrc}"
                      ${primaryImage?.srcset ? `srcset="${primaryImage.srcset}"` : ''}
                      alt="${imageAlt}"
                      width="${primaryImage?.width || 420}"
                      height="${primaryImage?.height || 513}"
                      loading="${index < 4 ? 'eager' : 'lazy'}"
                      class="motion-reduce media_product"
                      sizes="(max-width: 1024px) 220px, (min-width: 1024px) 360px, (min-width:1400px) 420px"
                    >
                  </a>
                </div>
              </div>
            </div>
            ${colorVariantBadge}
          </div>

          <div class="new_card_product_info">
            <div class="styleNoDate">
              ${styleNoDateContent}
            </div>
            <a href="${productUrl}">
              <h3 class="custom_card_heading">${productTitle.length > 61 ? productTitle.substring(0, 58) + '...' : productTitle}</h3>
            </a>

            <div class="btn_price">
              <div class="price">
                <div class="price__container" data-vendor="${product.vendor || ''}" data-proid="${productId}" data-price="${minPrice}">
                  <div class="price__regular">
                    <span class="price-item price-item--regular product_price">${formattedPrice}</span>
                    <span class="pricetax">(Incl. Of All Taxes)</span>
                  </div>
                </div>
              </div>
              <div class="showColorModal">
                <button
                  class="cloneBtn"
                  onclick="ATBModalOpen(event, '${productId}', '${colorVariantsIds}', '${product.select_size_guide?.value || ''}', 'category-page'),singleVariantSelectATB()"
                  aria-label="Button for Add to Bag Popup"
                >
                  <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg" class="add-to-bag-svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M11.2988 3.93606C11.2988 1.90046 9.60755 0.231445 7.5405 0.231445C5.47345 0.231445 3.7823 1.90053 3.7823 3.93593V5.46251H0.859289C0.504293 5.46251 0.23291 5.72707 0.23291 6.07314V15.6398C0.23291 17.3293 1.63178 18.693 3.3648 18.693H8.7463C8.52841 18.3161 8.34935 17.9139 8.21458 17.4919L3.36452 17.492C2.3205 17.492 1.48538 16.6778 1.48538 15.6601V6.70401L3.7823 6.70387V7.76225C3.7823 8.10833 4.05368 8.37289 4.40867 8.37289C4.76367 8.37289 5.03505 8.10833 5.03505 7.76225V6.70387H10.0461V7.76225C10.0461 8.10833 10.3175 8.37289 10.6725 8.37289C11.0275 8.37289 11.2988 8.10833 11.2988 7.76225V6.70387H13.5956V9.48248C13.7547 9.47015 13.9155 9.46387 14.0778 9.46387C14.3388 9.46387 14.5959 9.48011 14.8483 9.51163V6.07314C14.8483 5.72707 14.5769 5.46251 14.2219 5.46251L11.2988 5.46265V3.93606ZM7.5405 1.45243C8.91853 1.45243 10.046 2.57202 10.046 3.93572V5.4623H5.03498V3.93572C5.03498 2.57188 6.16246 1.45243 7.5405 1.45243Z" fill="#fff"/>
                    <path d="M14.0773 12.5402C13.6997 12.5402 13.3936 12.8462 13.3936 13.2239V14.9334H11.6841C11.3065 14.9334 11.0004 15.2394 11.0004 15.6171C11.0004 15.9947 11.3065 16.3008 11.6841 16.3008H13.3936V18.0103C13.3936 18.3879 13.6997 18.694 14.0773 18.694C14.455 18.694 14.761 18.3879 14.761 18.0103V16.3008H16.4705C16.8482 16.3008 17.1542 15.9947 17.1542 15.6171C17.1542 15.2394 16.8482 14.9334 16.4705 14.9334H14.761V13.2239C14.761 12.8462 14.455 12.5402 14.0773 12.5402Z" fill="#fff"/>
                  </svg>
                  <span>Add to bag</span>
                  <svg class="loading-spinner" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><style>.spinner_aj0A{transform-origin:center;animation:spinner_KYSC .75s infinite linear}@keyframes spinner_KYSC{100%{transform:rotate(360deg)}}</style><path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z" class="spinner_aj0A"/></svg>
                </button>
              </div>
            </div>
          </div>

          <div data-in-json="d${productId}" style="display: none">
            {
              "product_handle": "${product.handle}",
              "media": ${JSON.stringify(transformMediaFormat(product.images?.nodes || []))}
            }
          </div>
        </div>
      </div>
    </li>
  `;
}

function transformMediaFormat(images) {
  return images.map((image, index) => ({
    alt: image.altText || null,
    position: index + 1,
    media_type: 'image',
    src: image.src || image.url || '',
    width: image.width,
    height: image.height
  }));
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Product Grid Rendering                                                  │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function showLoadingOverlay() {
  const overlay = document.querySelector('.loading-overlay');
  if (overlay) overlay.closest('.collection').classList.add('loading');
}

function hideLoadingOverlay() {
  const overlay = document.querySelector('.loading-overlay');
  if (overlay) overlay.closest('.collection').classList.remove('loading');
}

function getIdsToRemove() {
  let idsToRemove = '';
  if(state.firstLoad) {
    idsToRemove += config.productIdRenderedInLiquid ? `,${config.productIdRenderedInLiquid}` : '';
  } else if (!hasFiltersBeenApplied()) {
    idsToRemove += config.bestsellersArray ? `,${config.bestsellersArray}` : '';
    idsToRemove += config.custom7thAnd10thProducts ? `,${config.custom7thAnd10thProducts}` : '';
  } else {
    // Remove all products
  }
  return idsToRemove;
}

function renderCollectionGrid(products, replace = false) {
  const productGrid = document.getElementById('product-grid');
  if (!productGrid) return;

  // Remove Duplicates from products - Products Rendered in Liquid, Bestsellers, Custom 7th and 10th Products
  let idsToRemove = getIdsToRemove();
  let sanitizedProducts = removeProductsByIds(products, idsToRemove);

  // Filter Bestseller and Custom 7th and 10th Products on Client Side
  let filteredBestsellers = filterProductsOnClientSide(state.bestsellers);
  let filteredCustom7thAnd10thProducts = filterProductsOnClientSide(state.custom7thAnd10thProducts);

  // Insert Promotional Products if total rendered products is less than 12
  let productsToRender = state.totalRenderedProducts < 12 ? insertPromotionalProducts(sanitizedProducts, filteredBestsellers, filteredCustom7thAnd10thProducts) : sanitizedProducts;
  let renderStartIndex = 0;

  // When filters are active, remove ALL products (including liquid-rendered ones)
  // because the filtered results are different
  if (replace) {
    const existingCards = productGrid.querySelectorAll('li');
    existingCards.forEach(card => card.remove());
    productsToRender = sanitizedProducts; // Render all filtered products
    setCachedProducts({
      products: productsToRender,
      filters: state.filters,
      pageInfo: state.pagination
    });
  }

  let productIndex = 0;

  // Get size filter for in-grid rendering
  const sizeFilter = state.filters.find(f => f.id === 'filter.v.option.size');
  const inGridSizeFilterPosition = config.inGridSizeFilter?.position || 4;

  // Get buzz in town section positions
  const buzzInTownMobilePosition = config.buzzInTownSection?.mobilePosition || 8;
  const buzzInTownDesktopPosition = config.buzzInTownSection?.desktopPosition || 8;
  const buzzInTownPosition = isMobile ? buzzInTownMobilePosition : buzzInTownDesktopPosition;

  // Check if in-grid size filter already exists in the DOM (from previous render)
  const existingInGridSizeFilter = document.querySelector('#product-grid .in-grid-size-filters');
  state.inGridSizeFilterInserted = !!existingInGridSizeFilter;

  // Check if buzz in town section already exists in the DOM (from previous render)
  const existingBuzzInTownSection = document.querySelector('#product-grid .buzz-in-town-section');
  state.buzzInTownSectionInserted = !!existingBuzzInTownSection;

  // Check if we should insert in-grid size filter before rendering any products
  // This handles the case where liquid products already reached the position
  if (!state.inGridSizeFilterInserted && config.inGridSizeFilter?.show && sizeFilter && state.totalRenderedProducts >= inGridSizeFilterPosition && isMobile && productsToRender.length > 0) {
    // Find the product at the target position in the DOM
    const allProductItems = Array.from(productGrid.querySelectorAll('li')).filter(item => {
      return !item.classList.contains('grid__item--skeleton') && !item.classList.contains('grid__item--full-width') && item.querySelector('.new_card_product');
    });
    const targetProductIndex = inGridSizeFilterPosition - 1;

    if (allProductItems.length > targetProductIndex) {
      const targetProduct = allProductItems[targetProductIndex];
      const inGridSizeFilterHTML = generateInGridSizeFilterHTML(sizeFilter);
      if (inGridSizeFilterHTML) {
        // Use insertAdjacentHTML to create a fresh element in the DOM
        // This ensures event listeners are properly attached
        targetProduct.insertAdjacentHTML('afterend', inGridSizeFilterHTML.trim());
        state.inGridSizeFilterInserted = true;
      }
    }
  }

  // Check if we should insert buzz in town section before rendering any products
  // This handles the case where liquid products already reached the position
  if (!state.buzzInTownSectionInserted && config.buzzInTownSection?.html && state.totalRenderedProducts >= buzzInTownPosition && productsToRender.length > 0 ) {
    // Find the product at the target position in the DOM
    const allProductItems = Array.from(productGrid.querySelectorAll('li')).filter(item => {
      return !item.classList.contains('grid__item--skeleton') && !item.classList.contains('grid__item--full-width') && !item.querySelector('.buzz-in-town-section') && item.querySelector('.new_card_product');
    });
    const targetProductIndex = buzzInTownPosition - 1;

    if (allProductItems.length > targetProductIndex) {
      const targetProduct = allProductItems[targetProductIndex];
      const buzzInTownHTML = generateBuzzInTownSectionHTML();
      if (buzzInTownHTML) {
        // Use insertAdjacentHTML to create a fresh element in the DOM
        // This ensures event listeners are properly attached
        targetProduct.insertAdjacentHTML('afterend', buzzInTownHTML.trim());
        state.buzzInTownSectionInserted = true;
        // Initialize swiper
        if(document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            initializeBuzzInTownSwiper();
          }, { once: true });
        } else {
          initializeBuzzInTownSwiper();
        }
      }
    }
  }

  // Render products with bestsellers inserted at specific positions
  for (const product of productsToRender) {
    // Apply color variant switching if color filter is active
    // This mirrors the Liquid template behavior of showing the matching color variant
    const productToRender = getProductWithMatchingColorVariant(product);

    // Skip rendering if no matching variant found (when color filter is active)
    if (productToRender === null) {
      continue;
    }

    // Render regular product first
    const globalIndex = renderStartIndex + productIndex;
    const productCardHTML = generateProductCardHTML(productToRender, globalIndex);
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = productCardHTML.trim();
    const productCard = tempDiv.firstElementChild;
    productGrid.appendChild(productCard);

    // Initialize swiper on hover for desktop
    const cardElement = productCard.querySelector('.new_card_product');
    if (cardElement && globalThis.innerWidth > 767) {
      cardElement.addEventListener('mouseenter', initializeProductCardSwiper);
      cardElement.addEventListener('mouseleave', destroyProductCardSwiper);
    }

    // Increment total rendered products count
    state.totalRenderedProducts++;
    productIndex++;

    // Check if we should insert in-grid size filter after this product
    // Only insert if we have enough products to reach the required position
    // Insert after the product at the specified position (e.g., after 4th product = position 4)
    // We check if totalRenderedProducts equals the position (meaning we just rendered the product at that position)
    if (
      !state.inGridSizeFilterInserted &&
      config.inGridSizeFilter?.show &&
      sizeFilter &&
      state.totalRenderedProducts === inGridSizeFilterPosition &&
      isMobile
    ) {
      // Insert in-grid size filter after the current product
      const inGridSizeFilterHTML = generateInGridSizeFilterHTML(sizeFilter);
      if (inGridSizeFilterHTML) {
        // Use insertAdjacentHTML to create a fresh element in the DOM
        // This ensures event listeners are properly attached
        productCard.insertAdjacentHTML('afterend', inGridSizeFilterHTML.trim());
        state.inGridSizeFilterInserted = true;
      }
    }

    // Check if we should insert buzz in town section after this product
    // Only insert if we have enough products to reach the required position
    // Insert after the product at the specified position (mobile: 4 or 8, desktop: 8)
    if (
      !state.buzzInTownSectionInserted &&
      config.buzzInTownSection?.html &&
      state.totalRenderedProducts === buzzInTownPosition
    ) {
      // Insert buzz in town section after the current product
      const buzzInTownHTML = generateBuzzInTownSectionHTML();
      if (buzzInTownHTML) {
        // Use insertAdjacentHTML to create a fresh element in the DOM
        // This ensures event listeners are properly attached
        productCard.insertAdjacentHTML('afterend', buzzInTownHTML.trim());
        state.buzzInTownSectionInserted = true;
        // Initialize swiper
        if(document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            initializeBuzzInTownSwiper();
          }, { once: true });
        } else {
          initializeBuzzInTownSwiper();
        }
      }
    }
  }
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Swiper Functions                                                        │
  └─────────────────────────────────────────────────────────────────────────┘
*/

const swiperInstances = new Map();

function initializeProductCardSwiper(event) {
  if (globalThis.innerWidth < 767) return;

  const parent = event.currentTarget;
  if (!parent) return;

  const swiperContainer = parent.querySelector('.collection-cardSwiper');
  if (!swiperContainer) return;

  const swiperWrapper = parent.querySelector('.swiper-wrapper');
  const productID = parent.dataset.productId;
  const dataDiv = parent.querySelector('[data-in-json]');

  if (dataDiv && !swiperWrapper.getAttribute('slides-built')) {
    try {
      const data = JSON.parse(dataDiv.innerText);
      let slides = '';

      data.media.forEach((media) => {
        const imageSource = media?.src?.trim();
        const altText = media?.alt?.trim() || 'Product image';
        const productHandle = data.product_handle?.trim();

        if (imageSource && media.media_type === 'image') {
          const separator = imageSource.includes('?') ? '&' : '?';
          const srcset = `${imageSource}${separator}width=220 220w, ${imageSource}${separator}width=360 360w, ${imageSource}${separator}width=420 420w`;
          const src = `${imageSource}${separator}width=420`;

          slides += `
            <div class="new_card_product_media swiper-slide" style="display: none;">
              ${productHandle ? `<a href="/products/${productHandle}">` : '<div>'}
                <img src="${src}" alt="${altText}" srcset="${srcset}" width="420" height="513" class="motion-reduce media_product" sizes="(max-width: 1024px) 220px, (min-width: 1024px) 360px, (min-width:1400px) 420px">
              ${productHandle ? '</a>' : '</div>'}
            </div>
          `;
        }
      });

      swiperWrapper.innerHTML += slides;
      swiperWrapper.setAttribute('slides-built', 'true');
    } catch (error) {
      console.error('Error parsing product data:', error);
    }
  }

  const sldImg = swiperContainer.querySelector('.sld-img');
  const swiperSlides = swiperContainer.querySelectorAll('.swiper-slide');
  if (sldImg) sldImg.style.display = 'none';
  swiperSlides.forEach(slide => slide.style.display = 'block');

  let swiper = swiperInstances.get(productID);
  if (!swiper && typeof Swiper !== 'undefined') {
    swiper = new Swiper(swiperContainer, {
      direction: 'horizontal',
      loop: true,
      autoplay: { delay: 1000, disableOnInteraction: false }
    });
    swiperInstances.set(productID, swiper);
  } else if (swiper) {
    swiper.autoplay.start();
  }
}

function destroyProductCardSwiper(event) {
  if (globalThis.innerWidth < 767) return;

  const parent = event.currentTarget;
  if (!parent) return;

  const swiperContainer = parent.querySelector('.collection-cardSwiper');
  if (!swiperContainer) return;

  const productID = parent.dataset.productId;
  const swiper = swiperInstances.get(productID);

  const sldImg = swiperContainer.querySelector('.sld-img');
  const swiperSlides = swiperContainer.querySelectorAll('.swiper-slide');
  if (sldImg) sldImg.style.display = 'block';
  swiperSlides.forEach(slide => slide.style.display = 'none');

  if (swiper) {
    swiper.autoplay.stop();
    swiper.slideTo(0, 1);
  }
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Infinte Scroll                                                          │
  └─────────────────────────────────────────────────────────────────────────┘
*/

let collectionInfiniteLoadingObserver = null;

function setupIntersectionObserver() {
  // Clean up existing observer
  if (collectionInfiniteLoadingObserver) {
    collectionInfiniteLoadingObserver.disconnect();
    collectionInfiniteLoadingObserver = null;
  }

  // Find the first skeleton loader
  const firstSkeleton = document.querySelector('.grid__item--skeleton');
  if (!firstSkeleton || !state.pagination.hasNextPage) {
    return;
  }

  // Create intersection observer
  collectionInfiniteLoadingObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !state.pagination.isLoading && state.pagination.hasNextPage) {
          loadMoreProducts();
        }
      });
    },
    {
      rootMargin: '1000px' // Start loading 200px before the skeleton is visible
    }
  );

  // Observe the first skeleton
  collectionInfiniteLoadingObserver.observe(firstSkeleton);
}

async function loadMoreProducts() {
  if (state.pagination.isLoading || !state.pagination.hasNextPage) return;

  state.pagination.isLoading = true;

  // Disconnect observer while loading
  if (collectionInfiniteLoadingObserver) {
    collectionInfiniteLoadingObserver.disconnect();
    collectionInfiniteLoadingObserver = null;
  }

  try {
    // Build current filters for pagination
    const graphqlFilters = [];
    Object.entries(state.activeFilters).forEach(([filterId, values]) => {
      values.forEach(value => {
        const filter = state.filters.find(f => f.id === filterId);
        if (filter) {
          const filterValue = filter.values.find(v => v.label === value);
          if (filterValue?.input) {
            graphqlFilters.push(JSON.parse(filterValue.input));
          }
        }
      });
    });

    if (state.priceRange.currentMin > state.priceRange.min || state.priceRange.currentMax < state.priceRange.max) {
      graphqlFilters.push({
        price: { min: state.priceRange.currentMin, max: state.priceRange.currentMax }
      });
    }

    // Use current sort state for pagination
    const result = await fetchCollectionData(state.pagination.cursor, graphqlFilters, state.currentSort.sortKey, state.currentSort.reverse);

    if (result.products.length === 0) {
      state.pagination.hasNextPage = false;
      state.pagination.isLoading = false;
      // Remove remaining skeletons
      document.querySelectorAll('.grid__item--skeleton').forEach(skeleton => skeleton.remove());
      return;
    }

    // Remove skeletons that will be replaced
    const skeletonsToRemove = Math.min(result.products.length, document.querySelectorAll('.grid__item--skeleton').length);
    document.querySelectorAll('.grid__item--skeleton').forEach((skeleton, index) => {
      if (index < skeletonsToRemove) {
        skeleton.remove();
      }
    });

    state.allProducts = [...state.allProducts, ...result.products];
    state.filters = result.filters;
    syncFiltersContext();
    renderCollectionGrid(result.products);

    state.pagination.cursor = result.pageInfo.endCursor;
    state.pagination.hasNextPage = result.pageInfo.hasNextPage;
    state.pagination.totalLoaded += result.products.length;

    // Generate skeleton loaders for next batch if there are more products
    if (state.pagination.hasNextPage) {
      generateSkeletonLoaders(Math.min(config.productsCount - state.allProducts.length, config.productsPerPage), false);
      setupIntersectionObserver();
    } else {
      // Remove any remaining skeletons
      document.querySelectorAll('.grid__item--skeleton').forEach(skeleton => skeleton.remove());
    }

    state.pagination.isLoading = false;

  } catch (error) {
    console.error('Error loading more products:', error);
    state.pagination.isLoading = false;
    // Re-setup observer on error
    if (state.pagination.hasNextPage) {
      setupIntersectionObserver();
    }
  }
}

globalThis.loadMoreProducts = loadMoreProducts;

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Main Initialization                                                     │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function renderNoProducts() {
  const productGrid = document.getElementById('product-grid');
  const collectionContainer = document.querySelector('.collection.page-width');

  if (!collectionContainer) return;

  // Hide product grid
  if (productGrid) {
    productGrid.style.display = 'none';
  }

  // Check if no-products div already exists
  let noProductsDiv = collectionContainer.querySelector('.no-products');

  if (!noProductsDiv) {
    // Create and add no-products div
    noProductsDiv = document.createElement('div');
    noProductsDiv.className = 'flex column gap-1 justify-center items-center h-full w-full no-products';
    noProductsDiv.innerHTML = `
      <h2 class="center fs-24 fw-600">
        No products found
      </h2>
      <h2 class="center fs-24 fw-600">
        Use fewer filters or
        <button class="underlined-link link fs-24 fw-600" onclick="clearAllFilters()">Remove All</button>
      </h2>
    `;
    collectionContainer.appendChild(noProductsDiv);
  }
}

function showProducts() {
  const productGrid = document.getElementById('product-grid');
  const collectionContainer = document.querySelector('.collection.page-width');

  if (!collectionContainer) return;

  // Show product grid
  if (productGrid) {
    productGrid.style.display = '';
  }

  // Remove no-products div if it exists
  const noProductsDiv = collectionContainer.querySelector('.no-products');
  if (noProductsDiv) {
    noProductsDiv.remove();
  }
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Insert Custom Positioned Products                                       │
  └─────────────────────────────────────────────────────────────────────────┘
*/
function insertPromotionalProducts(arrProductsToRender, arrBestSeller, arrUpSeller) {
  // Define insertion positions (0-based index)
  let bestSellerPositions = [3, 7, 11]; // 4th, 8th, 12th
  let upSellerPositions = [2, 5]; // 7th, 10th

  // Remove the first product from the bestsellers and bestseller positions
  // This is because the first product is the bestseller that is rendered in the liquid template
  arrBestSeller = arrBestSeller.slice(1);
  bestSellerPositions = bestSellerPositions.slice(0, -1);

  // Create a copy to work with
  const result = [...arrProductsToRender];


  // Collect all insertions
  const insertions = [];

  // Add BestSeller products
  arrBestSeller.forEach((product, index) => {
    if (index < bestSellerPositions.length) {
      insertions.push({
        position: bestSellerPositions[index],
        product: product
      });
    }
  });

  // Add UpSeller products
  arrUpSeller.forEach((product, index) => {
    if (index < upSellerPositions.length) {
      insertions.push({
        position: upSellerPositions[index],
        product: product
      });
    }
  });

  // Sort by position
  insertions.sort((a, b) => a.position - b.position);

  // Only insert if we have enough products to reach that position
  insertions.forEach(({ position, product }) => {
    // Only insert if the array is long enough to accommodate this position
    if (result.length >= position) {
      result.splice(position, 0, product);
    }
  });

  return result;
}

// This Function only Runs either on Initial Load or After Applying Filter
async function fetchAndRenderUI(invoker, filters = [], sortKey = null, reverse = false, invokerPayload = {}) {
  // Check cache first (only for filtered results, not initial load without filters)
  // Don't use cache when sorting changes
  const cachedResult = hasActiveFilters() && !sortKey ? getCachedProducts() : null;

  const fireGtm = () => {
    if (typeof FilterGtm !== 'function' || typeof pushQueryParamsToDataLayer !== 'function') return;
    // `requestIdleCallback` is polyfilled in `assets/global.js`
    globalThis.requestIdleCallback(() => {
      if (invoker === 'applyFilters') {
        pushQueryParamsToDataLayer('apply_filter_click');
      } else if (invoker === 'quickFilter') {
        pushQueryParamsToDataLayer('quick_filter_click', invokerPayload || {});
      } else if (invoker === 'applySort') {
        pushQueryParamsToDataLayer('sort_by_click', invokerPayload || {});
      } else if (invoker === 'pageload') {
        // pushQueryParamsToDataLayer("pageload_click");
      }
    });
  };

  let result;

  if (cachedResult) {
    // Use cached data
    result = cachedResult;
    // Check if cached result has no products
    if (result.products.length === 0) {
      renderNoProducts();
      fireGtm();
      return;
    }
  } else {
    try {
      result = await fetchCollectionData(null, filters, sortKey, reverse);
      if (result.products.length === 0) {
        console.warn(`🚀 || collection-grid.js:1549 || fetchAndRenderUI || No products found:`, result);
        renderNoProducts();
        fireGtm();
        return;
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      renderNoProducts();
      return;
    }
  }

  state.allProducts = result.products;

  // On first page load, update the global state with the fetched bestsellers only if:
  // - The result contains bestsellers,
  // - and the state's bestsellers array is either empty or outdated (length does not match the fetched bestsellers).
  // This avoids unnecessary overwrites and prevents picking up bestsellers from subsequent loads or filter changes.
  if (state.firstLoad && result.bestsellers.length > 0 && state.bestsellers.length !== result.bestsellers.length) {
    state.bestsellers = result.bestsellers;
  }

  // On first page load, update the global state with the fetched custom 7th and 10th products only if:
  // - The result contains custom 7th/10th products,
  // - and the state's list is not already up to date (length mismatch).
  // This ensures promotional product placement logic works as intended and avoids stale data.
  if (state.firstLoad && result.custom7thAnd10thProducts.length > 0 && state.custom7thAnd10thProducts.length !== result.custom7thAnd10thProducts.length) {
    state.custom7thAnd10thProducts = result.custom7thAnd10thProducts;
  }

  state.pagination.cursor = result.pageInfo.endCursor;
  state.pagination.hasNextPage = result.pageInfo.hasNextPage;
  state.pagination.totalLoaded = result.products.length;
  state.filters = result.filters;
  syncFiltersContext();

  // Show products grid and remove no-products message if products exist
  showProducts();

  // Remove skeletons and Full Width Sections that will be replaced
  let gridItemsToRemove = document.querySelectorAll('.grid__item--skeleton, .grid__item--full-width');
  gridItemsToRemove.forEach((gridItem, index) => {
    gridItem.remove();
  });

  renderCollectionGrid(result.products, hasActiveFilters());

  // Always re-render filters with updated counts
  // This ensures filter counts and disabled states are updated after applying filters
  renderFilters(result.filters);

  // Update quick filters UI to sync with active filters
  updateQuickFiltersUI();

  // Update active filter count after products are rendered
  updateActiveFilterCount();

  // Calculate and update product count based on active filters
  const filteredCount = calculateFilteredProductCount();
  if (filteredCount === null) {
    setProductCount(true); // Reset to default
  } else {
    setProductCount(false, filteredCount);
  }

  // Update URL query parameters after products and filters are rendered
  // Use requestIdleCallback to avoid blocking the main thread
  if (globalThis.requestIdleCallback) {
    globalThis.requestIdleCallback(() => {
      updateURLQueryParams();
    }, { timeout: 1000 });
  }

  // Generate skeleton loaders and set up intersection observer
  if (state.pagination.hasNextPage) {
    generateSkeletonLoaders(config.productsPerPage, false);
    setupIntersectionObserver();
  }

  // Dispatch event
  document.dispatchEvent(new CustomEvent('collectionProductsLoaded', {
    detail: {
      products: result.products,
      filters: result.filters,
      collectionHandle: config.collectionHandle,
      hasMore: state.pagination.hasNextPage
    }
  }));

  fireGtm();
}

async function init() {
  if (!config.collectionHandle) {
    console.error('Collection handle not configured');
    return;
  }

  // Parse URL query parameters and populate activeFilters
  let hasURLFilters = parseURLFilters();

  // Apply sort from URL if present
  if (state.currentSort.sortValue) {
    const mapped = mapSortValueToGraphQL(state.currentSort.sortValue);
    if (mapped) {
      state.currentSort.sortKey = mapped.sortKey;
      state.currentSort.reverse = mapped.reverse;
    }
  }

  // Build GraphQL filters from activeFilters (if any)
  const graphqlFilters = hasURLFilters ? buildGraphQLFiltersFromActiveFilters() : [];

  // Reset total rendered products count if filters are active (since grid is cleared)
  if (hasActiveFilters()) {
    state.totalRenderedProducts = 0;
    state.buzzInTownSectionInserted = false;
  } else {
    // If no filters, check if buzz in town section already exists from liquid rendering
    const existingBuzzInTownSection = document.querySelector('#product-grid .buzz-in-town-section');
    state.buzzInTownSectionInserted = !!existingBuzzInTownSection;
  }

  // Check if liquid-rendered products exist (only when filters are NOT active)
  const existingLiquidProducts = document.querySelectorAll('#product-grid li:not(.grid__item--skeleton)');
  const hasLiquidProducts = existingLiquidProducts.length > 0 && !hasActiveFilters();

  // Initialize swiper on first 4 liquid-rendered products (only if they exist)
  if (hasLiquidProducts) {
    existingLiquidProducts.forEach(productCard => {
      const cardElement = productCard.querySelector('.new_card_product');
      if (cardElement && globalThis.innerWidth > 767) {
        cardElement.addEventListener('mouseenter', initializeProductCardSwiper);
        cardElement.addEventListener('mouseleave', destroyProductCardSwiper);
      }
    });
  }

  // Fetch and render products via GraphQL (with sort if present)
  await fetchAndRenderUI('pageload', graphqlFilters, state.currentSort.sortKey, state.currentSort.reverse);

  state.firstLoad = false;

  // Close first accordion on desktop
  if (!isMobile) {
    const firstTab = document.querySelector('.neo-filter-tab');
    const firstContent = document.querySelector('.neo-filter-values-container');
    if (firstTab) firstTab.classList.remove('active');
    if (firstContent) firstContent.classList.remove('active');
  }
}

// Initialize on page load
init();
